import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  LinearProgress,
  Radio,
  RadioGroup,
  Snackbar,
  Step,
  StepConnector,
  stepConnectorClasses,
  StepLabel,
  Stepper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Cancel from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LightbulbOutlined from "@mui/icons-material/LightbulbOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SaveIcon from "@mui/icons-material/Save";
import SecurityIcon from "@mui/icons-material/Security";
import SpeedOutlined from "@mui/icons-material/SpeedOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { DatePicker, DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import axios from "axios";
import dayjs from "dayjs";
import GaugeComponent from "react-gauge-component";
import { FaFlask } from "react-icons/fa";
import { ShieldCheck } from "lucide-react";
import {
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Logo from "../../assets/svg/estbn-logo.svg";
import provider from "../../assets/svg/provider-img.svg";
import { newHost } from "../Categories/CategoryProfile";
import { useSelector } from "react-redux";

// ============================================================================
// 1) Constants
// ============================================================================

const C = {
  surface: "#E8E8E8",
  border: "#E8E8E8",
  teal: "#3FBEAC",
  tealDim: "#3FBEAC",
  purple: "#7E1BAD",
  amber: "#f5a623",
  blue: "#3b82f6",
  red: "#ef4444",
  text: "#000000",
  muted: "#535353",
  white: "#ffffff",
  bg: "#ffffff",
};

const STEPS = [
  "Basic Info",
  "Rules & Eligibility",
  "Budget & Funding",
  "Simulation",
  "Review & publish",
];

const STEP = {
  BASIC_INFO: 0,
  RULES: 1,
  BUDGET: 2,
  SIMULATION: 3,
  REVIEW: 4,
};

const ENUMS_URL = `${newHost}v1/incentives/enums`;
const VEHICLE_TYPES_URL = `${newHost}v1/incentives/enums/vehicle-types`;
const CATEGORIES_URL = `${newHost}v1/incentives/enums/categories`;
const STEP1_CREATE_URL = `${newHost}portal/incentives/wizard/step-1/basic-info`;
const STEP2_RULES_URL = `${newHost}portal/incentives/wizard/step-2/rules-eligibility`;
const STEP3_BUDGET_FUNDING_URL = `${newHost}portal/incentives/wizard/step-3/budget-funding`;
const STEP4_SIMULATION_URL = `${newHost}portal/incentives/wizard/step-4/simulation`;
const STEP5_OPTIONS_URL = `${newHost}portal/incentives/wizard/step-5/options`;
const STEP5_REVIEW_PUBLISH_URL = `${newHost}portal/incentives/wizard/step-5/review-publish`;
const INCENTIVE_URL = (id) => `${newHost}portal/incentives/${id}`;
const CREATE_INCENTIVE_URL = `${newHost}portal/incentives`;

const DRAFT_STORAGE_KEY = "incentive_draft_v2";

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    color: C.text,
    fontSize: 13,
    "& fieldset": { borderColor: C.border },
  },
};

const AUTOCOMPLETE_SX = {
  color: C.text,
  fontSize: 13,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: C.border },
  "& .MuiSvgIcon-root": { color: C.muted },
};

const STACK_BEHAVIOR_OPTIONS = [
  { id: "stackable", label: "Stackable" },
  { id: "non_stackable", label: "Non-stackable" },
  { id: "conditional", label: "Conditional" },
];

const MOCK_NOTIFICATION_RECIPIENTS = [
  { id: "marketing", label: "Marketing Team" },
  { id: "finance", label: "Finance Team" },
  { id: "provider_ops", label: "Provider Operations" },
];

const MOCK_FRAUD_SENSITIVITY = [
  { id: "low", label: "Low" },
  { id: "medium", label: "Medium" },
  { id: "high", label: "High" },
];

const MOCK_COST_MODELS = [
  { id: "fixed", label: "Fixed Cost per Redemption" },
  { id: "percentage", label: "Percentage of Order" },
];

const MOCK_STEP3_FALLBACKS = {
  wallet: [
    {
      id: "WALLET-MKTG-2026",
      label: "Incentive Marketing Budget",
    },
  ],
  burnRate: [{ id: "85", label: "85 %" }],
  forecastExhaustion: [{ id: "4", label: "4 days before depletion" }],
  protectedMargin: [{ id: "25000", label: "25,000 EGP" }],
  minRoi: [{ id: "2.5", label: "2.5x" }],
  maxLoss: [{ id: "5000", label: "5,000 EGP" }],
};

const MOCK_PUBLISH_OPTIONS = [
  { value: "now", label: "Publish Now", desc: "Publish immediately" },
  { value: "schedule", label: "Schedule for Later", desc: "Publish on a specific date" },
  { value: "draft", label: "Save as Draft", desc: "Continue later" },
];

// ============================================================================
// 2) Initial form state
// ============================================================================

const INITIAL_FORM_DATA = {
  // Step 1 - Basic Info
  title: "",
  internal_code: "",
  description: "",
  type: "",
  status: "",
  visibility: "",
  priority_level: "",
  trigger_type: "",
  scope_type: "",
  applies_to_categories: [],
  funding_source: "",
  starts_at: null,
  ends_at: null,
  recurrence: "",
  time_window_start: "",
  time_window_end: "",
  currency: "",
  timezone: "",
  workflow_notes: "",

  // Step 2 - Rules & Eligibility
  benefit: {
    benefit_type: "",
    // discount_type:"",
    percentage: "",
    amount: "",
    max_cap: "",
    min_cap: "",
    currency: "EGP",
  },
  eligibility: {
    min_order_value: "",
    customer_segment: [],
    vehicle_type: [],
    service_categories: [],
    region: [],
    order_channel: [],
  },
  usageLimits: {
    per_customer_limit: "",
    per_order_limit: "",
    total_redemptions_limit: "",
    requires_manual_approval_provider_funded: false,
  },
  stackBehavior: "",
  conflictRule: {
    excluded_items: [],
    conflict_resolution_strategy: "",
    protected_margin_threshold: "",
    // These three fields exist in the Step 2 API body but do not have UI fields yet.
    // Keep them empty until the backend/product confirms where their values come from.
    minimum_margin_amount: "",
    minimum_margin_percentage: "",
    cancellation_impact_limit: "",
    cancellation_policy: "",
  },

  // Step 3 - Budget & Funding (mock for now)
  budget: {
    total_budget: "",
    daily_budget_cap: "",
    monthly_budget_cap: "",
    redemption_cost_model: "",
    estimated_cost_per_redemption: "",
  },
  funding: {
    source: "",
    wallet: "",
    cost_center: "",
    platform_share_percentage: 60,
    provider_share_percentage: 40,
    provider_contribution_approval_required: true,
  },
  budgetControls: {
    burn_rate_threshold: "",
    forecast_exhaustion_alert_days: "",
    fraud_monitoring_sensitivity: "",
    auto_pause_when: "",
    auto_pause_margin_critical: true,
    auto_pause_burn_rate: true,
    notify_before_auto_pause: false,
  },
  safeguards: {
    protected_margin_target: "",
    protected_margin_target_amount: "",
    min_roi_target: "",
    max_allowed_loss: "",
    roi_calculation_base: "",
    approval_required_if_low_roi: true,
    approval_required_if_roi_below_target: true,
  },

  // Step 4 - Simulation
  simulation: {
    scenario_template: "",
    batch_mode: false,
    customer_type: "",
    channel: "",
    cart_items: [],
    taxes: "0",
    doorstep_fee: "0",
    existing_discounts: "0",
    loyalty_points_applied: "0",
  },

  // Step 5 - Review & Publish (mock UI, real publish API)
  publishOption: "",
  scheduledPublishDate: null,
  approvalRequired: true,
  approvalWorkflow: "",
  approver: "",
  notificationRecipients: [],
  rollback: {
    strategy: "",
    fallback_incentive: "",
    notes: "",
  },
  emergencyNotes: "",
};

// ============================================================================
// 3) Small helpers
// ============================================================================

function cloneInitialFormData() {
  return structuredClone(INITIAL_FORM_DATA);
}

function toOptions(list = []) {
  if (!Array.isArray(list)) return [];

  return list.map((item) => ({
    id: item?.value ?? item?.id,
    label: item?.label ?? item?.title ?? String(item?.value ?? item?.id ?? ""),
  }));
}

function toCategoryOptions(list = []) {
  if (!Array.isArray(list)) return [];

  return list.map((item) => ({
    id: item?.id ?? item?.value,
    label: item?.label ?? item?.title ?? String(item?.id ?? item?.value ?? ""),
  }));
}

function getErrorMessage(error) {
  const data = error?.response?.data;
  const fieldErrors = data?.errors
    ? Object.values(data.errors).flat().slice(0, 4).join(" • ")
    : "";

  return [data?.message || error?.message || "Something went wrong", fieldErrors]
    .filter(Boolean)
    .join(" — ");
}

function getByPath(object, path) {
  return path.split(".").reduce((current, key) => current?.[key], object);
}

function setPath(object, path, value) {
  const keys = path.split(".");

  if (keys.length === 1) {
    return { ...object, [keys[0]]: value };
  }

  const [firstKey, ...restKeys] = keys;
  const restPath = restKeys.join(".");

  return {
    ...object,
    [firstKey]: setPath(object?.[firstKey] ?? {}, restPath, value),
  };
}

function getEnumOptions(enums, keys = []) {
  for (const key of keys) {
    if (Array.isArray(enums?.[key]) && enums[key].length > 0) {
      return enums[key];
    }
  }

  return [];
}

function getDropdownOptions(dropdowns, keys = []) {
  for (const key of keys) {
    if (Array.isArray(dropdowns?.[key]) && dropdowns[key].length > 0) {
      return dropdowns[key];
    }
  }

  return [];
}

const REGION_FALLBACK_OPTIONS = [
  { id: "cairo", label: "Cairo" },
  { id: "giza", label: "Giza" },
];

function getSelectedLabels(selectedIds = [], options = []) {
  return selectedIds
    .map((id) => options.find((option) => option.id === id)?.label)
    .filter(Boolean);
}

function toNumberOrNull(value) {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function mapStackBehaviorToApi(value) {
  return value ?? null;
}

function buildStep2Payload(formData, options = {}) {
  const {
    vehicleTypes = [],
    serviceCategories = [],
    regions = [],
    incentiveId = null,
  } = options;

  const rewardType = formData.benefit?.benefit_type || null;
  const discountType = formData.benefit?.discount_type || null;
  const isPercentageDiscount = rewardType === "percentage_discount";
  const stackable = formData.stackBehavior === "stackable";

  const customerSegments = formData.eligibility?.customer_segment ?? [];
  const vehicleTypeIds = formData.eligibility?.vehicle_type ?? [];
  const serviceCategoryIds = formData.eligibility?.service_categories ?? [];
  const regionIds = formData.eligibility?.region ?? [];
  const orderChannels = formData.eligibility?.order_channel ?? [];
  const excludedItems = formData.conflictRule?.excluded_items ?? [];

  return {
    incentive_id: toNumberOrNull(incentiveId),
    reward_type: rewardType,
    // discount_type: discountType,
    discount_value: isPercentageDiscount
      ? toNumberOrNull(formData.benefit?.percentage)
      : toNumberOrNull(formData.benefit?.amount),
    max_discount_cap: toNumberOrNull(formData.benefit?.max_cap),
    min_discount_cap: toNumberOrNull(formData.benefit?.min_cap),
    min_order_value: toNumberOrNull(formData.eligibility?.min_order_value),

    stack_behavior: mapStackBehaviorToApi(formData.stackBehavior),
    is_stackable: stackable,

    customer_segments: customerSegments,
    target_customer_types: customerSegments,

    // The Step 2 API body expects the display values for these fields.
    vehicle_types: getSelectedLabels(vehicleTypeIds, vehicleTypes),
    service_categories: getSelectedLabels(serviceCategoryIds, serviceCategories),
    target_categories: getSelectedLabels(serviceCategoryIds, serviceCategories),
    regions: getSelectedLabels(regionIds, regions),
    target_regions: getSelectedLabels(regionIds, regions),

    order_channels: orderChannels,
    target_channels: orderChannels,

    per_customer_limit: toNumberOrNull(formData.usageLimits?.per_customer_limit),
    usage_limit_per_customer: toNumberOrNull(
      formData.usageLimits?.per_customer_limit
    ),
    per_order_limit: toNumberOrNull(formData.usageLimits?.per_order_limit),
    total_redemptions_limit: toNumberOrNull(
      formData.usageLimits?.total_redemptions_limit
    ),
    total_usage_limit: toNumberOrNull(
      formData.usageLimits?.total_redemptions_limit
    ),

    excluded_items: excludedItems,
    exclude_taxes: excludedItems.includes("taxes"),
    exclude_doorstep_fee: excludedItems.includes("doorstep_fee"),

    protected_margin_threshold: toNumberOrNull(
      formData.conflictRule?.protected_margin_threshold
    ),
    margin_threshold_percent: toNumberOrNull(
      formData.conflictRule?.protected_margin_threshold
    ),
    minimum_margin_amount: toNumberOrNull(
      formData.conflictRule?.minimum_margin_amount
    ),
    minimum_margin_percentage: toNumberOrNull(
      formData.conflictRule?.minimum_margin_percentage
    ),
    cancellation_impact_limit: toNumberOrNull(
      formData.conflictRule?.cancellation_impact_limit
    ),
    conflict_rule_strategy:
      formData.conflictRule?.conflict_resolution_strategy || null,
  };
}

function buildStep3Payload(formData, incentiveId = null) {
  return {
    incentive_id: toNumberOrNull(incentiveId),
    total_budget: toNumberOrNull(formData.budget?.total_budget),
    daily_budget_cap: toNumberOrNull(formData.budget?.daily_budget_cap),
    monthly_budget_cap: toNumberOrNull(formData.budget?.monthly_budget_cap),
    redemption_cost_model: formData.budget?.redemption_cost_model || null,
    estimated_cost_per_redemption: toNumberOrNull(
      formData.budget?.estimated_cost_per_redemption
    ),

    funding_source: formData.funding?.source || null,
    platform_share_percent: toNumberOrNull(
      formData.funding?.platform_share_percentage
    ),
    provider_share_percent: toNumberOrNull(
      formData.funding?.provider_share_percentage
    ),
    provider_contribution_approval_required: Boolean(
      formData.funding?.provider_contribution_approval_required
    ),
    funding_wallet_id: formData.funding?.wallet || null,
    cost_center: formData.funding?.cost_center || null,

    burn_rate_threshold_percent: toNumberOrNull(
      formData.budgetControls?.burn_rate_threshold
    ),
    forecast_exhaustion_alert_days: toNumberOrNull(
      formData.budgetControls?.forecast_exhaustion_alert_days
    ),
    fraud_monitoring_sensitivity:
      formData.budgetControls?.fraud_monitoring_sensitivity || null,
    auto_pause_when: formData.budgetControls?.auto_pause_when || null,
    auto_pause_on_margin_critical: Boolean(
      formData.budgetControls?.auto_pause_margin_critical
    ),
    auto_pause_on_burn_rate_exceeded: Boolean(
      formData.budgetControls?.auto_pause_burn_rate
    ),
    notify_before_auto_pause: Boolean(
      formData.budgetControls?.notify_before_auto_pause
    ),

    protected_margin_target_amount: toNumberOrNull(
      formData.safeguards?.protected_margin_target_amount
    ),
    min_roi_target: toNumberOrNull(formData.safeguards?.min_roi_target),
    max_allowed_loss: toNumberOrNull(formData.safeguards?.max_allowed_loss),
    roi_calculation_base:
      formData.safeguards?.roi_calculation_base || null,
    approval_required_if_low_roi: Boolean(
      formData.safeguards?.approval_required_if_low_roi
    ),
  };
}

function buildSimulationPayload(formData, incentiveId) {
  const simulation = formData.simulation ?? {};
  const benefitType = formData.benefit?.benefit_type;
  const discountType = benefitType === "percentage_discount" ? "percentage" : "fixed";

  return {
    incentive_id: toNumberOrNull(incentiveId),
    scenario_template: simulation.scenario_template || null,
    batch_mode: Boolean(simulation.batch_mode),
    customer_type: simulation.customer_type || null,
    channel: simulation.channel || null,
    cart_items: (simulation.cart_items ?? []).map((item) => ({
      product_id: toNumberOrNull(item?.product_id),
      name: item?.name || "",
      price: toNumberOrNull(item?.price),
      quantity: toNumberOrNull(item?.quantity),
    })),
    taxes: toNumberOrNull(simulation.taxes) ?? 0,
    doorstep_fee: toNumberOrNull(simulation.doorstep_fee) ?? 0,
    existing_discounts: toNumberOrNull(simulation.existing_discounts) ?? 0,
    loyalty_points_applied: toNumberOrNull(simulation.loyalty_points_applied) ?? 0,
    // Applied Incentives are read-only in Step 4 and come directly from Step 1.
    applied_incentives: [
      {
        type: formData.type || null,
        code: formData.internal_code || "",
      },
    ],
    rules_eligibility: {
      discount_type: discountType,
      discount_value:
        benefitType === "percentage_discount"
          ? toNumberOrNull(formData.benefit?.percentage)
          : toNumberOrNull(formData.benefit?.amount),
      max_discount_cap: toNumberOrNull(formData.benefit?.max_cap),
      min_order_value: toNumberOrNull(formData.eligibility?.min_order_value),
      margin_threshold_percent: toNumberOrNull(
        formData.conflictRule?.protected_margin_threshold
      ),
      is_stackable: formData.stackBehavior === "stackable",
      exclude_taxes: (formData.conflictRule?.excluded_items ?? []).includes("taxes"),
      exclude_doorstep_fee: (formData.conflictRule?.excluded_items ?? []).includes(
        "doorstep_fee"
      ),
    },
    budget: {
      total_budget: toNumberOrNull(formData.budget?.total_budget),
      platform_share_percent: toNumberOrNull(
        formData.funding?.platform_share_percentage
      ),
      provider_share_percent: toNumberOrNull(
        formData.funding?.provider_share_percentage
      ),
      funding_source: formData.funding?.source || null,
    },
  };
}

function formatTimeWindow(start24, end24) {
  if (!start24 || !end24) return "";

  const formatTime = (time) => dayjs(`2000-01-01 ${time}`).format("h:mm a");
  return `${formatTime(start24)} - ${formatTime(end24)}`;
}

function buildStep1Payload(formData) {
  return {
    title: formData.title,
    internal_code: formData.internal_code,
    type: formData.type,
    description: formData.description,
    status: formData.status,
    visibility: formData.visibility,
    priority_level: formData.priority_level,
    trigger_type: formData.trigger_type,
    scope_type: formData.scope_type,
    applies_to: formData.applies_to_categories.map((category) => category.id),
    funding_source: formData.funding_source,
    starts_at: formData.starts_at
      ? dayjs(formData.starts_at).startOf("day").format("YYYY-MM-DD HH:mm:ss")
      : null,
    ends_at: formData.ends_at
      ? dayjs(formData.ends_at).endOf("day").format("YYYY-MM-DD HH:mm:ss")
      : null,
    recurrence: formData.recurrence,
    time_window: formatTimeWindow(
      formData.time_window_start,
      formData.time_window_end
    ),
    currency: formData.currency,
    timezone: formData.timezone,
    workflow_notes: formData.workflow_notes,
  };
}

function buildConditions(formData) {
  const conditions = [];
  const eligibility = formData.eligibility;

  if (eligibility.min_order_value !== "") {
    conditions.push({
      condition_key: "min_order_value",
      operator: ">=",
      condition_value: {
        amount: Number(eligibility.min_order_value),
      },
    });
  }

  const multiFields = [
    ["customer_segment", eligibility.customer_segment],
    ["vehicle_type", eligibility.vehicle_type],
    ["service_categories", eligibility.service_categories],
    ["region", eligibility.region],
    ["order_channel", eligibility.order_channel],
  ];

  multiFields.forEach(([key, values], index) => {
    if (values.length > 0) {
      conditions.push({
        condition_key: key,
        operator: "in",
        condition_value: { values },
        priority: index + 2,
      });
    }
  });

  return conditions;
}

function mapFundingSourceToStep5(value) {
  if (value === "estbn") return "platform";
  if (value === "platform") return "platform";
  if (value === "provider") return "provider";
  if (value === "shared") return "shared";
  return value || null;
}

function buildStep5Payload(formData) {
  const benefitType = formData.benefit?.benefit_type;
  const discountType =
    benefitType === "percentage_discount" ? "percentage" : "fixed";

  const payload = {
    title: formData.title || null,
    type: formData.type || null,
    description: formData.description || null,
    publish_option: formData.publishOption || null,
    valid_from: formData.starts_at
      ? dayjs(formData.starts_at).startOf("day").format("YYYY-MM-DD HH:mm:ss")
      : null,
    valid_to: formData.ends_at
      ? dayjs(formData.ends_at).endOf("day").format("YYYY-MM-DD HH:mm:ss")
      : null,
    currency: formData.currency || formData.benefit?.currency || null,
    timezone: formData.timezone || null,
    rules_eligibility: {
      discount_type: discountType,
      discount_value:
        benefitType === "percentage_discount"
          ? toNumberOrNull(formData.benefit?.percentage)
          : toNumberOrNull(formData.benefit?.amount),
      max_discount_cap: toNumberOrNull(formData.benefit?.max_cap),
      min_order_value: toNumberOrNull(formData.eligibility?.min_order_value),
      margin_threshold_percent: toNumberOrNull(
        formData.conflictRule?.protected_margin_threshold
      ),
      is_stackable: formData.stackBehavior === "stackable",
    },
    budget: {
      total_budget: toNumberOrNull(formData.budget?.total_budget),
      daily_budget_cap: toNumberOrNull(formData.budget?.daily_budget_cap),
      funding_source: mapFundingSourceToStep5(formData.funding?.source),
      platform_share_percent: toNumberOrNull(
        formData.funding?.platform_share_percentage
      ),
    },
    controls_safeguards: {
      burn_rate_threshold_percent: toNumberOrNull(
        formData.budgetControls?.burn_rate_threshold
      ),
      auto_pause_on_burn_rate_exceeded: Boolean(
        formData.budgetControls?.auto_pause_burn_rate
      ),
      auto_pause_on_margin_critical: Boolean(
        formData.budgetControls?.auto_pause_margin_critical
      ),
    },
    rollback_strategy: formData.rollback?.strategy || null,
    notification_recipients: formData.notificationRecipients ?? [],
    approver_id:
      formData.approvalWorkflow === "auto_approve"
        ? null
        : toNumberOrNull(formData.approver),
    approval_workflow: formData.approvalWorkflow || null,
    emergency_notes: formData.emergencyNotes || null,
    rollback_notes: formData.rollback?.notes || null,
    fallback_incentive_id: toNumberOrNull(formData.rollback?.fallback_incentive),
  };

  if (formData.publishOption === "schedule") {
    payload.scheduled_publish_at = formData.scheduledPublishDate
      ? dayjs(formData.scheduledPublishDate).format("YYYY-MM-DD HH:mm:ss")
      : null;
  }

  return payload;
}

function getSavedId(response) {
  return (
    response?.data?.data?.id ??
    response?.data?.data?.incentive_id ??
    response?.data?.id ??
    response?.data?.incentive_id ??
    null
  );
}

function validateBasicInfo(formData) {
  const errors = {};

  const requiredTextFields = [
    ["title", "Title"],
    ["internal_code", "Internal Code"],
    ["type", "Incentive Type"],
    ["status", "Status"],
    ["visibility", "Visibility"],
    ["priority_level", "Priority Level"],
    ["trigger_type", "Trigger Type"],
    ["scope_type", "Scope Type"],
    ["funding_source", "Funding Source"],
    ["recurrence", "Recurrence"],
    ["currency", "Currency"],
    ["timezone", "Timezone"],
    ["starts_at", "Start Date"],
    ["ends_at", "End Date"],
    ["time_window_start", "From"],
    ["time_window_end", "To"],
  ];

  requiredTextFields.forEach(([key, label]) => {
    const value = formData[key];
    if (value === "" || value === null || value === undefined) {
      errors[key] = `${label} is required.`;
    }
  });

  if (formData.applies_to_categories.length === 0) {
    errors.applies_to_categories = "At least one category is required.";
  }

  if (formData.starts_at && formData.ends_at) {
    if (dayjs(formData.ends_at).isBefore(dayjs(formData.starts_at), "day")) {
      errors.ends_at = "End Date must be after Start Date.";
    }
  }

  return errors;
}

// ============================================================================
// 4) API hooks
// ============================================================================

function useIncentiveEnums(token) {
  const [rawEnums, setRawEnums] = useState(null);
  const [rawDropdowns, setRawDropdowns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return;

    let mounted = true;
    setLoading(true);

    axios
      .get(ENUMS_URL, {
        headers: { Authorization: token },
      })
      .then((response) => {
        if (!mounted) return;

        const data = response.data?.data ?? {};
        setRawEnums(data.enums ?? null);
        setRawDropdowns(data.dropdowns ?? null);
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err);
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [token]);

  const enums = useMemo(() => {
    if (!rawEnums) return {};

    return Object.keys(rawEnums).reduce((acc, key) => {
      acc[key] = toOptions(rawEnums[key]);
      return acc;
    }, {});
  }, [rawEnums]);

  const dropdowns = useMemo(() => {
    if (!rawDropdowns) return {};

    return Object.keys(rawDropdowns).reduce((acc, key) => {
      acc[key] = toOptions(rawDropdowns[key]);
      return acc;
    }, {});
  }, [rawDropdowns]);

  return { enums, dropdowns, rawEnums, loading, error };
}

function useVehicleTypes(token) {
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return;

    let mounted = true;
    setLoading(true);

    axios
      .get(VEHICLE_TYPES_URL, { headers: { Authorization: token } })
      .then((response) => {
        if (!mounted) return;

        const data = response.data?.data ?? response.data ?? [];
        const list =
          data?.vehicle_types ??
          data?.enum ??
          data?.items ??
          data;

        setVehicleTypes(toOptions(Array.isArray(list) ? list : []));
      })
      .catch((error) => {
        if (!mounted) return;
        setVehicleTypes([]);
        setError(error);
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [token]);

  return { vehicleTypes, loading, error };
}

function useCategories(token) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;

    let mounted = true;
    setLoading(true);

    axios
      .get(CATEGORIES_URL, {
        headers: { Authorization: token },
      })
      .then((response) => {
        if (!mounted) return;

        const list =
          response.data?.data?.enum ??
          response.data?.data?.items ??
          response.data?.data?.categories ??
          response.data?.data ??
          [];

        setCategories(toCategoryOptions(Array.isArray(list) ? list : []));
      })
      .catch(() => {
        if (!mounted) return;
        setCategories([]);
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [token]);

  return { categories, loading };
}

// ============================================================================
// 5) Reusable UI components
// ============================================================================
const AbuseProbabilityPreview = ({ data }) => {
  const riskPercentage = data?.risk_percentage;

  const hasPercentage =
    riskPercentage !== null &&
    riskPercentage !== undefined &&
    !Number.isNaN(Number(riskPercentage));

  const chartData = hasPercentage
    ? [
        {
          value: Number(riskPercentage),
        },
        {
          value: 100 - Number(riskPercentage),
        },
      ]
    : [
        {
          value: 100,
        },
      ];

  const level = data?.level || "Unknown";
  const risk = data?.risk || "";

  return (
    <Box width={160} height={160}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={chartData}
            startAngle={180}
            endAngle={0}
            innerRadius={60}
            outerRadius={80}
            dataKey="value"
          >
            <Cell fill="#A80101" />
            {hasPercentage && <Cell fill="#FFF4F0" />}
          </Pie>

          <text
            x="50%"
            y="40%"
            textAnchor="middle"
            fontSize="22"
            fill="#c62828"
            fontWeight="bold"
          >
            {level}
          </text>

          <text
            x="50%"
            y="55%"
            textAnchor="middle"
            fontSize="22"
            fill="#c62828"
            fontWeight="bold"
          >
            {risk}
          </text>
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
};
const QontoConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: { top: 16 },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: { borderColor: C.teal },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: { borderColor: C.teal },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: C.border,
    borderTopWidth: 2,
    borderRadius: 1,
  },
}));

const StepIconRoot = styled("div")(({ ownerState }) => ({
  width: 34,
  height: 34,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 13,
  fontWeight: 700,
  border: `2px solid ${
    ownerState.completed || ownerState.active ? C.teal : "transparent"
  }`,
  background: ownerState.completed
    ? C.teal
    : ownerState.active
      ? C.tealDim
      : C.surface,
  color: ownerState.completed
    ? C.bg
    : ownerState.active
      ? C.white
      : C.muted,
  transition: "all .3s",
}));

function StepIcon({ active, completed, icon }) {
  return (
    <StepIconRoot ownerState={{ active, completed }}>
      {completed ? <CheckCircleIcon sx={{ fontSize: 18 }} /> : icon}
    </StepIconRoot>
  );
}

function Card({ children, sx = {} }) {
  return (
    <Box
      sx={{
        background: C.bg,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
        p: 3,
        mb: 3,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function SectionLabel({ n, label }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        mb: 2.5,
      }}
    >
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: "50%",
          background: "#ECF9F7",
          border: `2px solid ${C.teal}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.teal }}>
          {n}
        </Typography>
      </Box>

      <Typography sx={{ fontSize: 15, fontWeight: 600, color: C.text }}>
        {label}
      </Typography>
    </Box>
  );
}

function FieldLabel({ children, required = false }) {
  return (
    <Typography
      sx={{ fontSize: 12, fontWeight: 500, color: C.muted, mb: 0.5 }}
    >
      {children}
      {required && <span style={{ color: C.red }}> *</span>}
    </Typography>
  );
}

function TextInput({
  label,
  required = false,
  value,
  onChange,
  multiline = false,
  rows,
  placeholder,
  error = false,
  helperText,
}) {
  return (
    <Box>
      {label && <FieldLabel required={required}>{label}</FieldLabel>}
      <TextField
        fullWidth
        size="small"
        multiline={multiline}
        rows={rows}
        placeholder={placeholder}
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        error={error}
        helperText={helperText}
        sx={INPUT_SX}
      />
    </Box>
  );
}

function EnumSelect({
  label,
  required = false,
  options = [],
  value,
  onChange,
  loading = false,
  placeholder = "Select option",
  helperText,
  error = false,
}) {
  const selected = options.find((option) => option.id === value) ?? null;

  return (
    <Box>
      {label && <FieldLabel required={required}>{label}</FieldLabel>}

      <Autocomplete
        size="small"
        sx={AUTOCOMPLETE_SX}
        options={options}
        loading={loading}
        value={selected}
        onChange={(_, option) => onChange(option?.id ?? "")}
        getOptionLabel={(option) => option?.label ?? ""}
        isOptionEqualToValue={(option, selectedOption) =>
          option?.id === selectedOption?.id
        }
        renderOption={(props, option) => (
          <li {...props} className="p-2">
            {option.label}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={loading ? "Loading..." : placeholder}
            error={error}
          />
        )}
      />

      {helperText && (
        <Typography sx={{ fontSize: 11, color: C.muted, mt: 0.5 }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
}

function MultiEnumSelect({
  label,
  required = false,
  options = [],
  value = [],
  onChange,
  placeholder = "Select option",
  helperText,
  disabled = false,
  loading = false,
  error = false,
}) {
  const selected = options.filter((option) => value.includes(option.id));

  return (
    <Box>
      {label && <FieldLabel required={required}>{label}</FieldLabel>}

      <Autocomplete
        multiple
        className="w-100"
        size="small"
        sx={AUTOCOMPLETE_SX}
        options={options}
        loading={loading}
        disabled={disabled}
        value={selected}
        onChange={(_, items) => onChange(items.map((item) => item.id))}
        getOptionLabel={(option) => option?.label ?? ""}
        isOptionEqualToValue={(option, selectedOption) =>
          option?.id === selectedOption?.id
        }
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.id}
              label={option.label}
              size="small"
              sx={{
                background: "#ECF9F7",
                color: C.teal,
                borderColor: C.teal,
                border: "1px solid",
                fontSize: 11,
              }}
            />
          ))
        }
        renderOption={(props, option) => (
          <li {...props} className="p-2">
            {option.label}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={loading ? "Loading..." : placeholder}
            error={error}
          />
        )}
      />

      {helperText && (
        <Typography sx={{ fontSize: 11, color: C.muted, mt: 0.5 }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
}

function QuickTips({ data }) {
  
  
  const tips = Array.isArray(data?.data?.data.quick_tips) ? data?.data?.data.quick_tips : [];
  return (
    <Card sx={{ my: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <LightbulbOutlined sx={{ fontSize: 16 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.text }}>Quick Tips</Typography>
      </Box>
      {tips.length === 0 ? (
        <Typography sx={{ fontSize: 12, color: C.muted }}>No tips available yet. Save this step to load the latest recommendations.</Typography>
      ) : tips.map((tip, index) => (
        <Box key={`${tip}-${index}`} sx={{ display: "flex", gap: 1, mb: index < tips.length - 1 ? 1.5 : 0 }}>
          <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: C.teal, mt: 0.7, flexShrink: 0 }} />
          <Typography sx={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>{tip}</Typography>
        </Box>
      ))}
    </Card>
  );
}

function ProjectedPerformance({ data }) {
  const performance = data?.data?.data?.projected_performance !== undefined ? data?.data?.data?.projected_performance : null
  console.log("performance", performance);
  
  return (
    <Card sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <TrendingUpIcon sx={{ color: C.text, fontSize: 18 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.text }}>Projected Performance</Typography>
      </Box>
      {!performance ? <Typography sx={{ fontSize: 12, color: C.muted }}>No performance projection available yet.</Typography> : [
        ["Estimated Redemptions", performance.estimated_redemptions ?? "—"],
        ["Estimated Spend", performance.estimated_spend ?? "—"],
        ["Estimated ROI", performance.estimated_roi ?? "—"],
        ["Status", performance.status ?? "—"],
      ].map(([label, value]) => (
        <Box key={label} sx={{ display: "flex", justifyContent: "space-between", mb: 1.2 }}>
          <Typography sx={{ fontSize: 12, color: C.muted }}>{label}</Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.text }}>{value}</Typography>
        </Box>
      ))}
    </Card>
  );
}

function ProfitabilityMeter({ data }) {
  const budgetForcast = data?.data?.data?.budget_forecast?.profitability_meter !== undefined ? data?.data?.data?.budget_forecast?.profitability_meter : null
  const score = Number(budgetForcast?.protected_margin_pct ?? 0);
  console.log("budgetForcast", budgetForcast);
  
  return (
    <Card sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <SpeedOutlined />
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.text }}>Profitability Meter</Typography>
      </Box>
      {!budgetForcast ? <Typography sx={{ fontSize: 12, color: C.muted }}>No profitability forecast available yet.</Typography> : (
        <>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
            <GaugeComponent type="semicircle" value={score} labels={{ valueLabel: { hide: true } }} />
          </Box>
          <Box sx={{ textAlign: "center", mb: 1.5 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 700 }}>{budgetForcast.status || "—"}</Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 500 }}>Protected Margin: {score}%</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.8 }}>
            <SecurityIcon sx={{ fontSize: 14, color: C.muted }} />
            <Typography sx={{ fontSize: 11, color: C.muted }}>{budgetForcast.notice || "—"}</Typography>
          </Box>
        </>
      )}
    </Card>
  );
}

function BusinessChecks({ state }) {
  const checks = [
    "What Applies",
    "Discount Value",
    "Cost Responsibility",
    "Conflict",
    "Stacking Logic",
    "Loss / Margin Risk",
  ];

  return (
    <Card sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <ShieldCheck size={18} />
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.text }}>
          Business Checks
        </Typography>
      </Box>

      {checks.map((label) => {
        const failed = state === "failed";
        const passed = state === "passed";

        return (
          <Box
            key={label}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.2,
            }}
          >
            <Typography sx={{ fontSize: 12, color: C.muted }}>
              {label}
            </Typography>

            {state === "pending" && (
              <Typography sx={{ fontSize: 12, color: C.muted }}>
                Pending
              </Typography>
            )}

            {passed && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <CheckCircleIcon sx={{ color: C.teal, fontSize: 15 }} />
                <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                  Passed
                </Typography>
              </Box>
            )}

            {failed && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Cancel sx={{ color: C.red, fontSize: 15 }} />
                <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                  Failed
                </Typography>
              </Box>
            )}
          </Box>
        );
      })}
    </Card>
  );
}

function RequiredBeforePublish({ step }) {
  return (
    <Card>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.text, mb: 2 }}>
        Required Before Publish
      </Typography>

      {STEPS.map((stepLabel, index) => (
        <Box
          key={stepLabel}
          sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}
        >
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                index < step
                  ? C.teal
                  : index === step
                    ? C.tealDim
                    : "transparent",
              border: `1.5px solid ${
                index <= step ? C.teal : C.border
              }`,
            }}
          >
            {index < step ? (
              <CheckCircleIcon sx={{ fontSize: 14, color: C.bg }} />
            ) : (
              <Typography
                sx={{
                  fontSize: 10,
                  color: index === step ? C.white : C.muted,
                  fontWeight: 700,
                }}
              >
                {index + 1}
              </Typography>
            )}
          </Box>

          <Typography
            sx={{
              fontSize: 12,
              color:
                index === step
                  ? C.teal
                  : index < step
                    ? C.text
                    : C.muted,
              fontWeight: index === step ? 600 : 400,
            }}
          >
            {stepLabel}
          </Typography>
        </Box>
      ))}
    </Card>
  );
}

function FundingSplit({ totalBudget, platformPct, providerPct }) {
  const platform = Number(platformPct) || 0;
  const providerValue = Number(providerPct) || 0;
  const budget = Number(totalBudget) || 0;

  const chartData = [
    { name: "Platform", value: platform, color: "#000000" },
    { name: "Provider", value: providerValue, color: "#7E1BAD" },
  ];

  const platformAmount = Math.round((budget * platform) / 100);
  const providerAmount = Math.round((budget * providerValue) / 100);

  return (
    <Grid
      container
      sx={{
        display: "flex",
        alignItems: "center",
        p: 2.5,
        borderRadius: 2,
        border: `1px solid ${C.border}`,
      }}
    >
      <Grid item xs={12} md={4}>
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: C.text }}>
          {platform}%
        </Typography>
        <Box
          sx={{
            width: "100%",
            height: 8,
            borderRadius: 2,
            background: C.text,
          }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            marginBlock: 1,
          }}
        >
          <img
            style={{
              backgroundColor: C.text,
              borderRadius: 5,
              padding: 7,
            }}
            src={Logo}
            alt="Platform"
          />
          <Box>
            <Typography sx={{ fontSize: 11, color: C.text, fontWeight: 500 }}>
              Platform
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
              {platformAmount.toLocaleString()} EGP
            </Typography>
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12} md={4} className="d-flex justify-content-center">
        <Box sx={{ width: 140, height: 140 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                innerRadius={40}
                outerRadius={55}
                dataKey="value"
              >
                {chartData.map((item) => (
                  <Cell key={item.name} fill={item.color} />
                ))}
              </Pie>
              <text
                x="50%"
                y="48%"
                textAnchor="middle"
                fontSize="12"
                fontWeight="700"
              >
                {budget.toLocaleString()}
              </text>
              <text x="50%" y="60%" textAnchor="middle" fontSize="10">
                EGP
              </text>
            </PieChart>
          </ResponsiveContainer>
        </Box>
      </Grid>

      <Grid item xs={12} md={4}>
        <Typography
          sx={{
            fontSize: 22,
            fontWeight: 700,
            color: C.purple,
            textAlign: "end",
          }}
        >
          {providerValue}%
        </Typography>
        <Box
          sx={{
            width: "100%",
            height: 8,
            borderRadius: 2,
            background: C.purple,
          }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            justifyContent: "end",
            marginBlock: 1,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 13, textAlign: "end" }}>
              Provider
            </Typography>
            <Typography
              sx={{ fontSize: 13, fontWeight: 600, textAlign: "end" }}
            >
              {providerAmount.toLocaleString()} EGP
            </Typography>
          </Box>
          <img
            style={{
              backgroundColor: C.purple,
              borderRadius: 5,
              padding: 7,
            }}
            src={provider}
            alt="Provider"
          />
        </Box>
      </Grid>
    </Grid>
  );
}

function getApiStepData(response) {
  return response??  {};
}

function SetupSummary({ step, formData, enums, simResult, simulationCompleted, stepResponses }) {
  const typeLabel =
    enums?.types?.find((item) => item.id === formData.type)?.label ?
    formData.type :
    "—";

  const fundingLabel =
    enums?.funding_sources?.find(
      (item) => item.id === formData.funding_source
    )?.label ?
    formData.funding_source :
    "—";
  const customerSegmentsLabel =
    (formData.eligibility?.customer_segment ?? [])
      .map(
        (segmentId) =>
          enums?.customer_segments?.find(
            (item) => item.id === segmentId
          )?.label
      )
      .filter(Boolean)
      .join(", ") ||
    "—";

  const step3FundingLabel =
    formData.funding?.source === "estbn"
      ? "Platform"
      : enums?.funding_sources?.find(
          (item) => item.id === formData.funding?.source
        )?.label || formData.funding?.source || "—";

  const step3RedemptionCostModelLabel =
    enums?.redemption_cost_models?.find(
      (item) => item.id === formData.budget?.redemption_cost_model
    )?.label || formData.budget?.redemption_cost_model || "—";

  return (
    <Box sx={{ position: "sticky", top: 24 }}>
      {step === STEP.BASIC_INFO && (
        <>
          <Card sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Typography sx={{ fontSize: 18 }}>▤</Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                Setup Summary
              </Typography>
            </Box>

            {[
              ["Type", stepResponses?.[STEP.BASIC_INFO]?.setup_summary?.type || "—"],
              ["Trigger", stepResponses?.[STEP.BASIC_INFO]?.setup_summary?.trigger || formData.trigger_type || "—"],
              ["Scope", stepResponses?.[STEP.BASIC_INFO]?.setup_summary?.scope || formData.scope_type || "—"],
              ["Applies To", stepResponses?.[STEP.BASIC_INFO]?.setup_summary?.applies_to || "—"],
              ["Funding", stepResponses?.[STEP.BASIC_INFO]?.setup_summary?.funding || "—"],
              ["Validity", stepResponses?.[STEP.BASIC_INFO]?.setup_summary?.validity || "—"],
              ["Recurrence", stepResponses?.[STEP.BASIC_INFO]?.setup_summary?.recurrence || "—"],
              ["Time Window", stepResponses?.[STEP.BASIC_INFO]?.setup_summary?.time_window || "—"],
              ["Status", stepResponses?.[STEP.BASIC_INFO]?.setup_summary?.status || formData.status || "—"],
            ].map(([label, value]) => (
              <Box key={label}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                    py: 1,
                  }}
                >
                  <Typography sx={{ fontSize: 12, color: C.muted }}>
                    {label}
                  </Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                    {value}
                  </Typography>
                </Box>
                <Box sx={{ borderBlockEnd: "1px solid #E8E8E8" }} />
              </Box>
            ))}
          </Card>

          <QuickTips data={stepResponses?.[STEP.BASIC_INFO]} />
          <RequiredBeforePublish step={step} />
        </>
      )}

      {step === STEP.RULES && (
        <>
          <Card sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Typography sx={{ fontSize: 18 }}>▤</Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                Setup Summary
              </Typography>
            </Box>

            {[
              ["Reward", stepResponses?.[STEP.RULES]?.setup_summary?.reward || formData.benefit?.benefit_type || "—"],
              ["Max Cap", stepResponses?.[STEP.RULES]?.setup_summary?.max_cap || formData.benefit?.max_cap || "—"],
              ["Min Order", stepResponses?.[STEP.RULES]?.setup_summary?.min_order || formData.eligibility?.min_order_value || "—"],
              ["Customer Segment", stepResponses?.[STEP.RULES]?.setup_summary?.customer_segment || "—"],
              ["Stack Behavior", stepResponses?.[STEP.RULES]?.setup_summary?.stack_behavior || formData.stackBehavior || "—"],
            ].map(([label, value]) => (
              <Box key={label}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    my: 1,
                    py: 1,
                  }}
                >
                  <Typography sx={{ fontSize: 12, color: C.muted }}>
                    {label}
                  </Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                    {value}
                  </Typography>
                </Box>
                <Box sx={{ borderBlockEnd: "1px solid #E8E8E8" }} />
              </Box>
            ))}
          </Card>
      
          <ConflictSidebar data={stepResponses?.[STEP.RULES]} />
          <AbuseRisk data={stepResponses?.[STEP.RULES]} />
          <QuickTips data={stepResponses?.[STEP.RULES]} />
          <RequiredBeforePublish step={step} />
        </>
      )}

      {step === STEP.BUDGET && (
        <>
          <Card sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Typography sx={{ fontSize: 18 }}>▤</Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                Setup Summary
              </Typography>
            </Box>

            {[
              [
                "Total Budget",
                stepResponses?.[STEP.BUDGET]?.setup_summary?.total_budget || "—",
              ],
              [
                "Daily Cap",
                stepResponses?.[STEP.BUDGET]?.setup_summary?.daily_cap || "—",
              ],
              [
                "Funding Source",
                stepResponses?.[STEP.BUDGET]?.setup_summary?.funding_source || "—",
              ],
              [
                "Cost Model",
                stepResponses?.[STEP.BUDGET]?.setup_summary?.cost_per_redemption || "—",
              ],
              [
                "Platform Share",
                stepResponses?.[STEP.BUDGET]?.setup_summary?.platform_share || "—",
              ],
              [
                "Provider Share",
                stepResponses?.[STEP.BUDGET]?.setup_summary?.provider_share || "—",
              ],
            ].map(([label, value]) => (
              <Box key={label}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                    py: 1,
                  }}
                >
                  <Typography sx={{ fontSize: 12, color: C.muted }}>
                    {label}
                  </Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                    {value}
                  </Typography>
                </Box>
                <Box sx={{ borderBlockEnd: "1px solid #E8E8E8" }} />
              </Box>
            ))}

            <Box sx={{ mt: 1 }}>
              <LinearProgress
                variant="determinate"
                value={Number(formData.funding.platform_share_percentage) || 0}
                sx={{
                  height: 5,
                  borderRadius: 3,
                  background: C.border,
                  "& .MuiLinearProgress-bar": {
                    background: C.text,
                    borderRadius: 3,
                  },
                }}
              />
            </Box>
          </Card>

          <ProjectedPerformance data={stepResponses?.[STEP.BUDGET]} />
          <ProfitabilityMeter data={stepResponses?.[STEP.BUDGET]} />
          <RequiredBeforePublish step={step} />
        </>
      )}

      {step === STEP.SIMULATION && (
        <>
          <Card sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Typography sx={{ fontSize: 18 }}>▤</Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                Setup Summary
              </Typography>
            </Box>
            {[
              ["Scenario", formData.simulation?.scenario_template || "—"],
              ["Customer Type", formData.simulation?.customer_type || "—"],
              ["Channel", formData.simulation?.channel || "—"],
              [
                "Cart Items",
                String((formData.simulation?.cart_items ?? []).length),
              ],
              ["Simulation", simulationCompleted ? "Completed" : "Pending"],
            ].map(([label, value]) => (
              <Box key={label}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                    py: 1,
                  }}
                >
                  <Typography sx={{ fontSize: 12, color: C.muted }}>
                    {label}
                  </Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                    {value}
                  </Typography>
                </Box>
                <Box sx={{ borderBlockEnd: "1px solid #E8E8E8" }} />
              </Box>
            ))}
          </Card>
          <BusinessChecks state={simulationCompleted ? "passed" : "pending"} />
          <RequiredBeforePublish step={step} />
        </>
      )}

      {step === STEP.REVIEW && (
        <>
          <Card sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Typography sx={{ fontSize: 18 }}>▤</Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                Publish Summary
              </Typography>
            </Box>

            {[
              ["Publish Option", formData.publishOption || "—"],
              [
                "Approval Required",
                formData.approvalRequired ? "Yes" : "No",
              ],
              ["Approver", formData.approver || "—"],
              ["Status", formData.status || "—"],
            ].map(([label, value]) => (
              <Box key={label}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                    py: 1,
                  }}
                >
                  <Typography sx={{ fontSize: 12, color: C.muted }}>
                    {label}
                  </Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                    {value}
                  </Typography>
                </Box>
                <Box sx={{ borderBlockEnd: "1px solid #E8E8E8" }} />
              </Box>
            ))}
          </Card>

          <RequiredBeforePublish step={step} />
        </>
      )}
    </Box>
  );
}

function ConflictSidebar({ data }) {
  const review = data?.data?.data?.mini_conflict_review;
  const conflicts = review?.conflicts ?? [];
  return (
    <Card sx={{ my: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <WarningAmberIcon sx={{ color: C.amber, fontSize: 16 }} />
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Mini Conflict Review</Typography>
      </Box>
      {!review ? <Typography sx={{ fontSize: 11, color: C.muted }}>No conflict review available yet.</Typography> : (
        <>
          <Typography sx={{ fontSize: 11, color: C.muted, mb: 1 }}>{review.notice || "—"}</Typography>
          {review.has_conflicts && conflicts.length ? conflicts.map((item, index) => (
            <Box key={`${item.name}-${index}`} sx={{ display: "flex", justifyContent: "space-between", gap: 1, py: 0.7 }}>
              <Typography sx={{ fontSize: 11 }}>{item.name}</Typography>
              <Chip size="small" label={item.severity || item.level || "—"} />
            </Box>
          )) : <Typography sx={{ fontSize: 11, color: C.teal }}>No conflicts detected.</Typography>}
        </>
      )}
    </Card>
  );
}

function AbuseRisk({ data }) {
  
  
  const risk = data?.data?.data?.abuse_risk !== undefined ? data?.data?.data?.abuse_risk  :  {}
  
  return (
    <Card sx={{ my: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <SecurityIcon sx={{ color: C.red, fontSize: 16 }} />
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Abuse Risk</Typography>
      </Box>
      {!risk ? (
        <Typography sx={{ fontSize: 11, color: C.muted }}>No abuse-risk assessment available yet.</Typography>
      ) : (
        <>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color:  risk.level === "High" ? C.red : C.text }}>{ risk.level || "—"}</Typography>
          <Typography sx={{ fontSize: 11, color: C.muted, mt: 0.5 }}>{ risk.notice || "—"}</Typography>
        </>
      )}
    </Card>
  );
}

function SmartWarnings({ data }) {
  const warnings = Array.isArray(data?.data.data?.smart_warnings) ? data?.data?.data?.smart_warnings.filter((item) => item.active) : [];
  return (
    <Box sx={{ background: "#FFFAEA", border: "1px solid #FEB91E4D", borderRadius: 2, p: 2.5 }}>
      <Box sx={{ display: "flex", alignItems: "start", gap: 2, mb: 2 }}>
        <WarningAmberIcon sx={{ color: "#FEB91E", fontSize: 18 }} />
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.amber }}>Smart Warnings</Typography>
          <Typography sx={{ fontSize: 12, color: C.muted }}>Action recommended before proceeding.</Typography>
        </Box>
      </Box>
      {warnings.length === 0 ? <Typography sx={{ fontSize: 12, color: C.muted }}>No active warnings returned by the server.</Typography> : (
        <Grid container spacing={2}>
          {warnings.map((warning) => (
            <Grid item xs={12} sm={6} md={3} key={warning.id}>
              <Box sx={{ background: C.white, width: "100%", height: "100%", p: 2, borderRadius: 2 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{warning.title || "—"}</Typography>
                <Typography sx={{ fontSize: 11, color: C.muted, mb: 0.5 }}>{warning.description || "—"}</Typography>
                <Typography sx={{ fontSize: 11, color: C.teal }}>{warning.action_label || "—"}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

function RulesEligibilityStep({
  formData,
  set,
  enums,
  dropdowns,
  vehicleTypes = [],
  enumsLoading,
  vehicleTypesLoading,
  apiData,
  categories,
  categoriesLoading,
}) {
  const vehicleTypeOptions = vehicleTypes;

  const apiRegionOptions = getEnumOptions(enums, ["regions"]);
  const regionOptions = apiRegionOptions.length
    ? apiRegionOptions
    : getDropdownOptions(dropdowns, ["regions"]).length
      ? getDropdownOptions(dropdowns, ["regions"])
      : REGION_FALLBACK_OPTIONS;

  const orderChannelOptions =
    getEnumOptions(enums, ["channels"]).length
      ? getEnumOptions(enums, ["channels"])
      : getDropdownOptions(dropdowns, ["channels"]);

  const excludedItemOptions =
    getEnumOptions(enums, ["excluded_items"]).length
      ? getEnumOptions(enums, ["excluded_items"])
      : getDropdownOptions(dropdowns, ["excluded_items"]);

  const serviceCategoryOptions = getDropdownOptions(dropdowns, ["categories"]);

  const apiStackOptions = getEnumOptions(enums, ["stacking_policies"]);
  const supportedStackValues = ["stackable", "non_stackable", "conditional"];
  const stackBehaviorOptions = supportedStackValues.map((value) =>
    apiStackOptions.find((option) => option.id === value) ?? {
      id: value,
      label:
        value === "non_stackable"
          ? "Non-stackable"
          : value === "conditional"
            ? "Conditional"
            : "Stackable",
    }
  );

  return (
    <Box>
      <Card>
        <SectionLabel n={1} label="Reward Configuration" />

        <Grid container spacing={2.5}>
          <Grid item xs={12} md={3}>
            <EnumSelect
              required
              label="Reward Type"
              loading={enumsLoading}
              options={enums?.benefit_types}
              value={formData.benefit.benefit_type}
              onChange={(value) => set("benefit.benefit_type", value)}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FieldLabel required>Discount Value</FieldLabel>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="discount"
              value={
                formData.benefit.benefit_type === "percentage_discount"
                  ? formData.benefit.percentage
                  : formData.benefit.amount
              }
              onChange={(event) =>
                set(
                  formData.benefit.benefit_type === "percentage_discount"
                    ? "benefit.percentage"
                    : "benefit.amount",
                  event.target.value
                )
              }
              sx={INPUT_SX}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FieldLabel>Maximum Discount Cap</FieldLabel>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="max discount"
              value={formData.benefit.max_cap}
              onChange={(event) => set("benefit.max_cap", event.target.value)}
              sx={INPUT_SX}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FieldLabel>Minimum Order Value (reward-level)</FieldLabel>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="min order"
              value={formData.benefit.min_cap}
              onChange={(event) => set("benefit.min_cap", event.target.value)}
              sx={INPUT_SX}
            />
          </Grid>

          <Grid item xs={12} md={8}>
            <FieldLabel required>Stack Behavior</FieldLabel>
            <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
              {stackBehaviorOptions.map((option) => (
                <FormControlLabel
                  key={option.id}
                  control={
                    <Radio
                      checked={formData.stackBehavior === option.id}
                      onChange={() => set("stackBehavior", option.id)}
                      sx={{
                        color: C.border,
                        "&.Mui-checked": { color: C.text },
                        p: 0.5,
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: 12 }}>
                      {option.label}
                    </Typography>
                  }
                />
              ))}
            </Box>
          </Grid>
        </Grid>

        {formData.stackBehavior === "non_stackable" && (
          <Alert
            severity="info"
            sx={{
              mt: 1.5,
              background: "#ECF9F7",
              color: C.text,
              "& .MuiAlert-icon": { color: C.teal },
              border: `1px solid ${C.teal}80`,
              borderRadius: 1.5,
              fontSize: 12,
            }}
          >
            Only one offer can apply per order
          </Alert>
        )}
      </Card>

      <Card>
        <SectionLabel n={2} label="Eligibility Rules" />

        <Grid container spacing={2.5}>
          <Grid item xs={12} md={6}>
            <FieldLabel required>Minimum Order Value</FieldLabel>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="Min order value"
              value={formData.eligibility.min_order_value}
              onChange={(event) =>
                set("eligibility.min_order_value", event.target.value)
              }
              sx={INPUT_SX}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <MultiEnumSelect
              required
              label="Customer Segment"
              loading={enumsLoading}
              options={enums?.customer_segments}
              value={formData.eligibility.customer_segment}
              onChange={(value) => set("eligibility.customer_segment", value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <MultiEnumSelect
              required
              label="Vehicle Type"
              loading={vehicleTypesLoading}
              options={vehicleTypeOptions}
              value={formData.eligibility.vehicle_type}
              onChange={(value) => set("eligibility.vehicle_type", value)}
              helperText={!vehicleTypeOptions.length && !vehicleTypesLoading ? "No vehicle types returned by the vehicle-types API." : undefined}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <MultiEnumSelect
              required
              label="Service Categories"
              loading={enumsLoading}
              options={serviceCategoryOptions}
              value={formData.eligibility.service_categories}
              onChange={(value) => set("eligibility.service_categories", value)}
              helperText={!serviceCategoryOptions.length ? "No categories returned by the enums API." : undefined}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <MultiEnumSelect
              required
              label="Region"
              loading={enumsLoading}
              options={regionOptions}
              value={formData.eligibility.region}
              onChange={(value) => set("eligibility.region", value)}
              helperText={!regionOptions.length ? "No regions returned by the enums API." : undefined}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <MultiEnumSelect
              required
              label="Order Channel"
              loading={enumsLoading}
              options={orderChannelOptions}
              value={formData.eligibility.order_channel}
              onChange={(value) => set("eligibility.order_channel", value)}
              helperText={!orderChannelOptions.length ? "No order channels returned by the enums API." : undefined}
            />
          </Grid>
        </Grid>
      </Card>

      <Card>
        <SectionLabel
          n={3}
          label="Usage Limits — Control how often the incentive can be used."
        />

        <Grid container spacing={2.5}>
          <Grid item xs={12} md={4}>
            <FieldLabel required>Per Customer Limit</FieldLabel>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="customer limit"
              value={formData.usageLimits.per_customer_limit}
              onChange={(event) =>
                set("usageLimits.per_customer_limit", event.target.value)
              }
              sx={INPUT_SX}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FieldLabel required>Per Order Limit</FieldLabel>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="order limit"
              value={formData.usageLimits.per_order_limit}
              onChange={(event) =>
                set("usageLimits.per_order_limit", event.target.value)
              }
              sx={INPUT_SX}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FieldLabel required>Total Redemptions Limit</FieldLabel>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="Total Redemptions Limit"
              value={formData.usageLimits.total_redemptions_limit}
              onChange={(event) =>
                set("usageLimits.total_redemptions_limit", event.target.value)
              }
              sx={INPUT_SX}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Switch
                size="small"
                checked={
                  formData.usageLimits
                    .requires_manual_approval_provider_funded
                }
                onChange={(event) =>
                  set(
                    "usageLimits.requires_manual_approval_provider_funded",
                    event.target.checked
                  )
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: C.teal,
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    background: C.teal,
                  },
                }}
              />
              <Typography sx={{ fontSize: 12, color: C.muted }}>
                Require manual approval for provider-funded redemptions
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Card>

      <Card>
        <SectionLabel
          n={4}
          label="Exclusions & Safeguards — Protect margin and prevent misuse."
        />

        <Grid container spacing={2.5}>
          <Grid item xs={12} md={4}>
            <MultiEnumSelect
              required
              label="Excluded Items"
              loading={enumsLoading}
              options={excludedItemOptions}
              value={formData.conflictRule.excluded_items}
              onChange={(value) => set("conflictRule.excluded_items", value)}
              helperText={!excludedItemOptions.length ? "No excluded-item options returned by the enums API." : undefined}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <EnumSelect
              label="Conflict Resolution Strategy"
              loading={enumsLoading}
              options={enums?.conflict_resolution_strategies}
              value={formData.conflictRule.conflict_resolution_strategy}
              onChange={(value) =>
                set("conflictRule.conflict_resolution_strategy", value)
              }
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <EnumSelect
              required
              label="Protected Margin Threshold"
              options={[
                { id: "18", label: "18%" },
                { id: "20", label: "20%" },
              ]}
              value={formData.conflictRule.protected_margin_threshold}
              onChange={(value) =>
                set("conflictRule.protected_margin_threshold", value)
              }
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <EnumSelect
              required
              label="Cancellation Policy"
              loading={enumsLoading}
              options={enums?.cancellation_behaviors}
              value={formData.conflictRule.cancellation_policy}
              onChange={(value) =>
                set("conflictRule.cancellation_policy", value)
              }
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2.5 }}>
          <SmartWarnings data={apiData} />
        </Box>
      </Card>
    </Box>
  );
}


// ============================================================================
// 8) Step 3 - Budget & Funding (REAL API)
// ============================================================================

function SpendForecastCard({ forecast, totalBudget }) {
  const timeline = forecast?.timeline ?? [];
  const chartData = timeline.map((item) => ({
    date: item.label || `Day ${item.day}`,
    projectedBurn: item.projected_burn,
    remainingBudget: item.remaining_budget,
  }));
  const budget = Number(totalBudget) || 0;

  return (
    <Box sx={{ p: 3, borderRadius: 2, bgcolor: "#fff", width: "100%" }}>
      {!forecast ? (
        <Typography sx={{ fontSize: 12, color: C.muted }}>No budget forecast available yet.</Typography>
      ) : (
        <>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 2 }}>
            <Typography sx={{ fontSize: 12, color: C.muted }}>Daily Burn: <strong>{forecast.daily_burn_rate || "—"}</strong></Typography>
            <Typography sx={{ fontSize: 12, color: C.muted }}>Depletion: <strong>{forecast.forecast_depletion || "—"}</strong></Typography>
          </Box>
          <Box sx={{ width: "100%", height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <XAxis dataKey="date" />
                <YAxis tickFormatter={(value) => `${value / 1000}K`} />
                <Tooltip />
                {budget > 0 && <ReferenceLine y={budget} stroke="#7ED7D0" strokeDasharray="4 4" label="Budget Limit" />}
                <Line dataKey="projectedBurn" stroke="#00B8B0" strokeWidth={3} dot={false} name="Projected Burn" />
                <Line dataKey="remainingBudget" stroke="#00B8B0" strokeWidth={2} strokeDasharray="6 6" dot={false} name="Remaining Budget" />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </>
      )}
    </Box>
  );
}

function BasicInfoStep({
  formData,
  set,
  enums,
  categories,
  enumsLoading,
  categoriesLoading,
  errors,
}) {
  const typeOptions = enums?.types ?? [];

  return (
    <Box>
      <Card sx={{ mb: 0 }}>
        <SectionLabel n={1} label="Basic Information" />

        <Grid container spacing={2.5}>
          <Grid item xs={12} md={6}>
            <TextInput
              required
              label="Title"
              placeholder="e.g. Weekend Oil Offer"
              value={formData.title}
              onChange={(value) => set("title", value)}
              error={Boolean(errors.title)}
              helperText={errors.title}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextInput
              required
              label="Internal Code"
              placeholder="e.g. INC-OFF-WKND-2026"
              value={formData.internal_code}
              onChange={(value) => set("internal_code", value)}
              error={Boolean(errors.internal_code)}
              helperText={errors.internal_code}
            />
          </Grid>

          <Grid item xs={12}>
            <FieldLabel required>Incentive Type</FieldLabel>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.5 }}>
              {typeOptions.map((option) => {
                const selected = formData.type === option.id;

                return (
                  <Chip
                    key={option.id}
                    label={option.label}
                    onClick={() => set("type", option.id)}
                    variant={selected ? "filled" : "outlined"}
                    sx={{
                      cursor: "pointer",
                      fontSize: 12,
                      background: selected ? C.teal : "transparent",
                      color: selected ? C.bg : C.muted,
                      borderColor: selected ? C.teal : C.border,
                      fontWeight: selected ? 700 : 400,
                    }}
                  />
                );
              })}
            </Box>
            {errors.type && (
              <Typography sx={{ fontSize: 11, color: C.red, mt: 0.5 }}>
                {errors.type}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <TextInput
              multiline
              rows={3}
              label="Description"
              placeholder="Enter a short description"
              value={formData.description}
              onChange={(value) => set("description", value)}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <EnumSelect
              required
              label="Status"
              loading={enumsLoading}
              options={enums?.statuses}
              value={formData.status}
              onChange={(value) => set("status", value)}
              error={Boolean(errors.status)}
              helperText={errors.status}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <EnumSelect
              required
              label="Visibility"
              loading={enumsLoading}
              options={enums?.visibilities}
              value={formData.visibility}
              onChange={(value) => set("visibility", value)}
              error={Boolean(errors.visibility)}
              helperText={errors.visibility}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <EnumSelect
              required
              label="Priority Level"
              loading={enumsLoading}
              options={enums?.priorities}
              value={formData.priority_level}
              onChange={(value) => set("priority_level", value)}
              error={Boolean(errors.priority_level)}
              helperText={errors.priority_level}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <EnumSelect
              required
              label="Trigger Type"
              loading={enumsLoading}
              options={enums?.trigger_types}
              value={formData.trigger_type}
              onChange={(value) => set("trigger_type", value)}
              error={Boolean(errors.trigger_type)}
              helperText={errors.trigger_type}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <EnumSelect
              required
              label="Scope Type"
              loading={enumsLoading}
              options={enums?.scope_types}
              value={formData.scope_type}
              onChange={(value) => set("scope_type", value)}
              error={Boolean(errors.scope_type)}
              helperText={errors.scope_type}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <EnumSelect
              required
              label="Funding Source"
              loading={enumsLoading}
              options={enums?.funding_sources}
              value={formData.funding_source}
              onChange={(value) => set("funding_source", value)}
              error={Boolean(errors.funding_source)}
              helperText={errors.funding_source}
            />
          </Grid>

          <Grid item xs={12}>
            <FieldLabel required>Applies To (Categories)</FieldLabel>
            <Autocomplete
              multiple
              size="small"
              sx={AUTOCOMPLETE_SX}
              options={categories}
              loading={categoriesLoading}
              value={formData.applies_to_categories}
              onChange={(_, items) => set("applies_to_categories", items)}
              getOptionLabel={(option) => option?.label ?? ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderTags={(tags, getTagProps) =>
                tags.map((category, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={category.id}
                    label={category.label}
                    size="small"
                    sx={{
                      background: "#ECF9F7",
                      color: C.teal,
                      border: "1px solid",
                      borderColor: C.teal,
                      fontSize: 11,
                    }}
                  />
                ))
              }
              renderOption={(props, option) => (
                <li {...props} className="p-2">
                  {option.label}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    categoriesLoading ? "Loading..." : "Select categories"
                  }
                  error={Boolean(errors.applies_to_categories)}
                />
              )}
            />
            {errors.applies_to_categories && (
              <Typography sx={{ fontSize: 11, color: C.red, mt: 0.5 }}>
                {errors.applies_to_categories}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} md={3}>
            <FieldLabel required>Start Date</FieldLabel>
            <DatePicker
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  error: Boolean(errors.starts_at),
                  helperText: errors.starts_at,
                },
              }}
              value={formData.starts_at ? dayjs(formData.starts_at) : null}
              onChange={(date) =>
                set(
                  "starts_at",
                  date && date.isValid() ? date.format("YYYY-MM-DD") : null
                )
              }
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FieldLabel required>End Date</FieldLabel>
            <DatePicker
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  error: Boolean(errors.ends_at),
                  helperText: errors.ends_at,
                },
              }}
              value={formData.ends_at ? dayjs(formData.ends_at) : null}
              onChange={(date) =>
                set(
                  "ends_at",
                  date && date.isValid() ? date.format("YYYY-MM-DD") : null
                )
              }
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <EnumSelect
              required
              label="Recurrence"
              loading={enumsLoading}
              options={enums?.recurrence_patterns}
              value={formData.recurrence}
              onChange={(value) => set("recurrence", value)}
              error={Boolean(errors.recurrence)}
              helperText={errors.recurrence}
            />
          </Grid>

          <Grid item xs={6} md={1.5}>
            <FieldLabel required>From</FieldLabel>
            <TextField
              fullWidth
              size="small"
              type="time"
              value={formData.time_window_start}
              onChange={(event) =>
                set("time_window_start", event.target.value)
              }
              error={Boolean(errors.time_window_start)}
              sx={INPUT_SX}
            />
          </Grid>

          <Grid item xs={6} md={1.5}>
            <FieldLabel required>To</FieldLabel>
            <TextField
              fullWidth
              size="small"
              type="time"
              value={formData.time_window_end}
              onChange={(event) => set("time_window_end", event.target.value)}
              error={Boolean(errors.time_window_end)}
              sx={INPUT_SX}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <EnumSelect
              required
              label="Currency"
              loading={enumsLoading}
              options={enums?.currencies}
              value={formData.currency}
              onChange={(value) => set("currency", value)}
              error={Boolean(errors.currency)}
              helperText={errors.currency}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <EnumSelect
              required
              label="Timezone"
              loading={enumsLoading}
              options={enums?.timezones}
              value={formData.timezone}
              onChange={(value) => set("timezone", value)}
              error={Boolean(errors.timezone)}
              helperText={errors.timezone}
            />
          </Grid>

          <Grid item xs={12}>
            <TextInput
              multiline
              rows={2}
              label="Workflow Notes (optional)"
              placeholder="Internal notes or context..."
              value={formData.workflow_notes}
              onChange={(value) => set("workflow_notes", value)}
            />
          </Grid>
        </Grid>
      </Card>
    </Box>
  );
}

// ============================================================================
// 7) Step 2 - Rules & Eligibility (REAL API)
// ============================================================================


function BudgetFundingStep({ formData, set, enums, dropdowns, enumsLoading, apiData }) {
  const redemptionCostModelOptions = getEnumOptions(enums, [
    "redemption_cost_models",
  ]);
  const fraudSensitivityOptions = getEnumOptions(enums, [
    "fraud_monitoring_sensitivities",
    "fraud_sensitivities",
  ]);
  const autoPauseOptions = getEnumOptions(enums, [
    "auto_pause_triggers",
    "auto_pause_when",
  ]);
  const roiBaseOptions = getEnumOptions(enums, [
    "roi_calculation_bases",
    "roi_bases",
  ]);
  const protectedMarginOptions = getEnumOptions(enums, [
    "protected_margin_options",
  ]);
  const burnRateOptions = getEnumOptions(enums, [
    "burn_rate_thresholds",
  ]);
  const forecastExhaustionOptions = getEnumOptions(enums, [
    "forecast_exhaustion_alert_days",
    "exhaustion_alert_days",
  ]);
  const minRoiOptions = getEnumOptions(enums, [
    "min_roi_targets",
  ]);
  const maxLossOptions = getEnumOptions(enums, [
    "max_allowed_losses",
  ]);
  const walletOptions = getEnumOptions(enums, ["wallets"]);
  const costCenterOptions = getEnumOptions(enums, ["cost_centers"]);


  const isSharedFunding = formData.funding.source === "shared";

  const handleFundingSourceChange = (source) => {
    set("funding.source", source);

    if (source === "estbn") {
      set("funding.platform_share_percentage", 100);
      set("funding.provider_share_percentage", 0);
      return;
    }

    if (source === "provider") {
      set("funding.platform_share_percentage", 0);
      set("funding.provider_share_percentage", 100);
      return;
    }

    // Shared starts from the mock UI's 60/40 split and remains editable.
    set("funding.platform_share_percentage", 60);
    set("funding.provider_share_percentage", 40);
  };

  const handlePlatformShareChange = (event) => {
    const rawValue = Number(event.target.value);
    const value = Number.isFinite(rawValue)
      ? Math.min(100, Math.max(0, rawValue))
      : 0;

    set("funding.platform_share_percentage", value);
    set("funding.provider_share_percentage", 100 - value);
  };

  const handleWalletChange = (walletId) => {
    set("funding.wallet", walletId || "");
  };

  const totalBudget = Number(formData.budget.total_budget) || 0;
  const remainingBudgetPreview = totalBudget;

  const formatDate = (value) =>
    value && dayjs(value).isValid() ? dayjs(value).format("MMM D, YYYY") : "—";

  const budgetPeriod =
    formData.starts_at || formData.ends_at
      ? `${formatDate(formData.starts_at)} - ${formatDate(formData.ends_at)}`
      : "Set Start / End Date in Basic Info";



  const approvalMatrix = Array.isArray(apiData?.data?.data?.approval_matrix) ? apiData?.data?.data?.approval_matrix : []
  const budgetForcast = apiData?.data?.data?.budget_forecast !== undefined ? apiData?.data?.data?.budget_forecast : null
 const performance = apiData?.data?.data?.projected_performance !== undefined ? apiData?.data?.data?.projected_performance : null 
  return (

    <Box>
      <Card>
        <SectionLabel n={1} label="Budget Setup" />

        <Grid container spacing={2.5}>
          <Grid item xs={12} md={4}>
            <TextInput
              required
              label="Total Budget"
              placeholder="Total Budget"
              value={formData.budget.total_budget}
              onChange={(value) => set("budget.total_budget", value)}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FieldLabel>Remaining Budget (Preview)</FieldLabel>
            <TextField
              fullWidth
              size="small"
              value={remainingBudgetPreview ? `${remainingBudgetPreview.toLocaleString()} EGP` : ""}
              placeholder="Remaining Budget"
              InputProps={{ readOnly: true }}
              sx={INPUT_SX}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextInput
              required
              label="Daily Budget Cap"
              placeholder="Daily Budget"
              value={formData.budget.daily_budget_cap}
              onChange={(value) => set("budget.daily_budget_cap", value)}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextInput
              required
              label="Monthly Budget Cap"
              placeholder="Monthly Budget"
              value={formData.budget.monthly_budget_cap}
              onChange={(value) => set("budget.monthly_budget_cap", value)}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <EnumSelect
              required
              label="Redemption Cost Model"
              loading={enumsLoading}
              options={redemptionCostModelOptions}
              value={formData.budget.redemption_cost_model}
              onChange={(value) => set("budget.redemption_cost_model", value)}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextInput
              required
              label="Estimated Cost per Redemption"
              placeholder="Estimated Cost"
              value={formData.budget.estimated_cost_per_redemption}
              onChange={(value) =>
                set("budget.estimated_cost_per_redemption", value)
              }
            />
          </Grid>
        </Grid>

        <Alert
          icon={<CalendarTodayIcon />}
          sx={{
            mt: 2,
            background: "#ECF9F7",
            color: C.text,
            "& .MuiAlert-icon": { color: C.teal },
            border: `1px solid #3FBEAC80`,
            borderRadius: 1.5,
            fontSize: 14,
          }}
        >
          Budget Period: {budgetPeriod}
        </Alert>
      </Card>

      <Card>
        <SectionLabel n={2} label="Funding Responsibility" />

        <Grid container spacing={2.5}>
          <Grid item xs={12} md={6}>
            <FieldLabel required>Funding Source</FieldLabel>
            <RadioGroup
              row
              value={formData.funding.source}
              onChange={(event) => handleFundingSourceChange(event.target.value)}
              sx={{ mt: 0.5 }}
            >
              {[
                { id: "estbn", label: "Platform" },
                { id: "provider", label: "Provider" },
                { id: "shared", label: "Shared" },
              ].map((option) => (
                <FormControlLabel
                  key={option.id}
                  value={option.id}
                  control={
                    <Radio
                      sx={{
                        color: C.border,
                        "&.Mui-checked": { color: C.teal },
                        p: 0.5,
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: 12, color: C.text }}>
                      {option.label}
                    </Typography>
                  }
                />
              ))}
            </RadioGroup>
          </Grid>

          <Grid item xs={12} md={6}>
            <EnumSelect
              required
              label="Funding Wallet"
              loading={enumsLoading}
              options={walletOptions}
              value={formData.funding.wallet}
              onChange={handleWalletChange}
              placeholder="Funding Wallet"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <EnumSelect
              required
              label="Cost Center"
              loading={enumsLoading}
              options={costCenterOptions}
              value={formData.funding.cost_center}
              onChange={(value) => set("funding.cost_center", value)}
              placeholder="Cost Center"
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2.5 }}>
          <Typography
            sx={{ fontSize: 13, fontWeight: 500, color: C.muted, mb: 1.5 }}
          >
            Funding Split
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              mb: 1.5,
              flexWrap: "wrap",
            }}
          >
            <TextField
              size="small"
              type="number"
              label="Platform %"
              value={formData.funding.platform_share_percentage}
              disabled={!isSharedFunding}
              onChange={handlePlatformShareChange}
              sx={{ minWidth: 120, ...INPUT_SX }}
            />

            <TextField
              size="small"
              type="number"
              label="Provider %"
              value={formData.funding.provider_share_percentage}
              disabled
              sx={{ minWidth: 120, ...INPUT_SX }}
            />
          </Box>

          <FundingSplit
            totalBudget={totalBudget}
            platformPct={formData.funding.platform_share_percentage}
            providerPct={formData.funding.provider_share_percentage}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2 }}>
          <Switch
            checked={formData.funding.provider_contribution_approval_required}
            onChange={(event) =>
              set(
                "funding.provider_contribution_approval_required",
                event.target.checked
              )
            }
            size="small"
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": { color: C.teal },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                background: C.teal,
              },
            }}
          />
          <Typography sx={{ fontSize: 12, color: C.muted }}>
            Provider Contribution Approval Required
          </Typography>
        </Box>
      </Card>

      <Card>
        <SectionLabel n={3} label="Budget Control & Alerts" />

        <Grid container spacing={2.5}>
          <Grid item xs={12} md={3}>
            <EnumSelect
              label="Burn Rate Threshold"
              options={burnRateOptions}
              value={formData.budgetControls.burn_rate_threshold}
              onChange={(value) =>
                set("budgetControls.burn_rate_threshold", value)
              }
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <EnumSelect
              label="Forecast Exhaustion Alert"
              options={forecastExhaustionOptions}
              value={formData.budgetControls.forecast_exhaustion_alert_days}
              onChange={(value) =>
                set("budgetControls.forecast_exhaustion_alert_days", value)
              }
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <EnumSelect
              label="Fraud Monitoring Sensitivity"
              loading={enumsLoading}
              options={fraudSensitivityOptions}
              value={formData.budgetControls.fraud_monitoring_sensitivity}
              onChange={(value) =>
                set("budgetControls.fraud_monitoring_sensitivity", value)
              }
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <EnumSelect
              label="Auto Pause When"
              loading={enumsLoading}
              options={autoPauseOptions}
              value={formData.budgetControls.auto_pause_when}
              onChange={(value) => set("budgetControls.auto_pause_when", value)}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
          {[
            [
              "Auto Pause When Margin Risk is Critical",
              "budgetControls.auto_pause_margin_critical",
            ],
            [
              "Auto Pause When Burn Rate Exceeds Threshold",
              "budgetControls.auto_pause_burn_rate",
            ],
            ["Notify Before Auto Pause", "budgetControls.notify_before_auto_pause"],
          ].map(([label, path]) => (
            <Grid
              item
              xs={12}
              md={4}
              key={path}
              sx={{ my: 1, display: "flex", justifyContent: "center" }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Switch
                  size="small"
                  checked={Boolean(getByPath(formData, path))}
                  onChange={(event) => set(path, event.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: C.teal },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      background: C.teal,
                    },
                  }}
                />
                <Typography sx={{ fontSize: 12, color: C.muted }}>
                  {label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Card>

      <Card>
        <SectionLabel n={4} label="Exclusions & Safeguards" />

        <Grid container spacing={2.5}>
          <Grid item xs={12} md={3}>
            <EnumSelect
              label="Protected Margin"
              options={protectedMarginOptions}
              value={formData.safeguards.protected_margin_target_amount}
              onChange={(value) =>
                set("safeguards.protected_margin_target_amount", value)
              }
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <EnumSelect
              label="Minimum ROI Target"
              options={minRoiOptions}
              value={formData.safeguards.min_roi_target}
              onChange={(value) => set("safeguards.min_roi_target", value)}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <EnumSelect
              label="Maximum Allowed Loss"
              options={maxLossOptions}
              value={formData.safeguards.max_allowed_loss}
              onChange={(value) => set("safeguards.max_allowed_loss", value)}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <EnumSelect
              label="ROI Calculation Base"
              loading={enumsLoading}
              options={roiBaseOptions}
              value={formData.safeguards.roi_calculation_base}
              onChange={(value) =>
                set("safeguards.roi_calculation_base", value)
              }
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
          <Switch
            checked={Boolean(formData.safeguards.approval_required_if_low_roi)}
            onChange={(event) =>
              set("safeguards.approval_required_if_low_roi", event.target.checked)
            }
            size="small"
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": { color: C.teal },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                background: C.teal,
              },
            }}
          />
          <Typography sx={{ fontSize: 12, color: C.muted }}>
            Approval Required if ROI Below Target
          </Typography>
        </Box>

        <Box
          sx={{
            mt: 2.5,
            background: "#ECF9F7",
            border: `1px solid ${C.teal}`,
            borderRadius: 2,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TrendingUpIcon sx={{ color: C.teal, fontSize: 20 }} />
            <Box>
              <Typography sx={{ fontSize: 11, color: C.muted }}>
                Based on current forecasts, this incentive is expected to achieve
              </Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: C.text }}>
                {performance?.estimated_roi || "—"}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ borderInlineEnd: "2px solid #BCBCBC", p: 2 }} />

          <Box>
            <Typography sx={{ fontSize: 11, color: C.muted }}>
              Estimated Spend
            </Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: C.text }}>
              {performance?.estimated_spend || "—"}
            </Typography>
          </Box>

          <Button
            size="small"
            endIcon={<ArrowRightAltIcon />}
            sx={{
              fontSize: 12,
              color: C.text,
              borderColor: C.teal,
              border: "1px solid #E8E8E8",
              borderRadius: 1,
              px: 2,
              backgroundColor: "#FFFFFF",
              textTransform: "none",
            }}
          >
            View ROI Breakdown
          </Button>
        </Box>
      </Card>

      <Card>
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <SectionLabel n={5} label="Approval Matrix" />
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["Role / Department", "Condition", "Required"].map((header) => (
                      <TableCell
                        key={header}
                        sx={{ color: C.muted, fontSize: 11, borderColor: C.border }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(approvalMatrix ?? []).map((item, index) => (
                    <TableRow
                      key={`${item.role_department}-${index}`}
                      sx={{ "&:last-child td": { border: 0 } }}
                    >
                      <TableCell sx={{ color: C.text, fontSize: 12, borderColor: C.border }}>
                        {item.role_department || "—"}
                      </TableCell>
                      <TableCell sx={{ color: C.muted, fontSize: 12, borderColor: C.border }}>
                        {item.condition || "—"}
                      </TableCell>
                      <TableCell sx={{ borderColor: C.border }}>
                        <CheckCircleOutlineIcon sx={{ color: C.teal, fontSize: 18 }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Grid>

          <Grid item xs={12} md={1} sx={{ display: { xs: "none", md: "block" } }}>
            <Box sx={{ height: "100%", borderInlineEnd: "2px solid #E8E8E8" }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionLabel n={6} label="Budget Forecast" />
            <SpendForecastCard forecast={budgetForcast} totalBudget={totalBudget} />
          </Grid>
        </Grid>
      </Card>
    </Box>
  );
}

// ============================================================================
// ============================================================================
// 9) Step 4 - Simulation (REAL API)
// ============================================================================

function SimulationStep({
  formData,
  set,
  enums,
  rawEnums,
  running,
  simResult,
  simulationCompleted,
  onRunSimulation,
}) {
  const scenarios = Array.isArray(rawEnums?.simulation_scenarios)
    ? rawEnums.simulation_scenarios
    : [];

  const selectedScenario =
    scenarios.find(
      (scenario) => scenario.value === formData.simulation?.scenario_template
    ) || null;

  const channelOptions = enums?.channels ?? [];

  const customerTypeOptions = Array.from(
    new Set(
      scenarios
        .map((scenario) => scenario?.context?.customer_type)
        .filter(Boolean)
    )
  ).map((value) => ({
    id: value,
    label:
      value === "returning"
        ? "Returning"
        : value === "new"
          ? "New"
          : value === "vip"
            ? "VIP"
            : value,
  }));

  const applyScenario = (scenario) => {
    if (!scenario) {
      set("simulation.scenario_template", "");
      set("simulation.customer_type", "");
      set("simulation.channel", "");
      set("simulation.cart_items", []);
      set("simulation.taxes", "0");
      set("simulation.doorstep_fee", "0");
      set("simulation.existing_discounts", "0");
      set("simulation.loyalty_points_applied", "0");
      return;
    }

    const scenarioChannel = scenario.context?.channel || "";
    const normalizedChannel =
      channelOptions.some((option) => option.id === scenarioChannel)
        ? scenarioChannel
        : scenarioChannel === "web"
          ? "web_portal"
          : scenarioChannel;

    set("simulation.scenario_template", scenario.value || "");
    set("simulation.customer_type", scenario.context?.customer_type || "");
    set("simulation.channel", normalizedChannel);
    set("simulation.cart_items", scenario.cart_items ?? []);
    set("simulation.taxes", String(scenario.context?.taxes ?? 0));
    set("simulation.doorstep_fee", String(scenario.context?.doorstep_fee ?? 0));
    set(
      "simulation.existing_discounts",
      String(scenario.context?.existing_discounts ?? 0)
    );
    set(
      "simulation.loyalty_points_applied",
      String(scenario.context?.loyalty_points_applied ?? 0)
    );
  };

  const updateCartItem = (index, field, value) => {
    const next = [...(formData.simulation?.cart_items ?? [])];
    next[index] = { ...next[index], [field]: value };
    set("simulation.cart_items", next);
  };

  const addCartItem = () => {
    const next = [
      ...(formData.simulation?.cart_items ?? []),
      { product_id: "", name: "", price: "", quantity: 1 },
    ];
    set("simulation.cart_items", next);
  };

  const removeCartItem = (index) => {
    set(
      "simulation.cart_items",
      (formData.simulation?.cart_items ?? []).filter(
        (_, itemIndex) => itemIndex !== index
      )
    );
  };

  const result = simResult ?? null;
  const isFailed = result?.status === "failed" || result?.simulation_passed === false;
  const isSuccessful =
    result?.status === "success" ||
    result?.status === "passed" ||
    result?.simulation_status === "passed" ||
    result?.simulation_passed === true ||
    result?.passed === true;

  const failureAnalysis = Array.isArray(result?.failure_analysis)
    ? result.failure_analysis
    : [];

  const optimizationHints = Array.isArray(result?.suggested_optimization_hints)
    ? result.suggested_optimization_hints
    : [];

  const abusePreview = result?.abuse_probability_preview ?? null;
  const rerunOptions = Array.isArray(result?.re_run_options)
    ? result.re_run_options
    : [];

  const validationChecklist = Array.isArray(result?.simulation_validation_checklist)
    ? result.simulation_validation_checklist
    : [];

  return (
    <Box>
      <Alert
        icon={<FaFlask />}
        sx={{
          mb: 2.5,
          backgroundColor: "#ECF9F7",
          color: C.text,
          "& .MuiAlert-icon": { color: C.teal },
          border: `1px solid ${C.teal}`,
          borderRadius: 1.5,
        }}
      >
        <AlertTitle sx={{ color: C.text, fontSize: 13 }}>
          Test Incentive Before Publish
        </AlertTitle>
        <Typography sx={{ fontSize: 12, color: C.muted }}>
          Run the configured scenario against the incentive rules and budget saved in the previous steps.
        </Typography>
      </Alert>

      <Card>
        <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
 <SectionLabel n={1} label="Scenario Builder" />
 <Box >
   
            <Typography variant="caption">Applied Incentives in This Scenario</Typography>
            <Box border={"1px solid #D8D8D8"} borderRadius={2} p={1}>
              <Typography color="#535353" variant="body2">{enums?.types?.find((option) => option.id === formData.type)?.label ||
                formData.type ||
                "—"}</Typography>
                <Typography fontWeight={600}>{formData.title || "—"}</Typography>



            </Box>
   
         
 </Box>
  
        </Box>
       
  
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={4}>
            <EnumSelect
              required
              label="Scenario Template"
              options={scenarios.map((scenario) => ({
                id: scenario.value,
                label: scenario.label ?? scenario.title ?? scenario.value,
              }))}
              value={formData.simulation?.scenario_template}
              onChange={(value) => {
                const scenario = scenarios.find((item) => item.value === value);
                applyScenario(scenario);
              }}
              loading={!scenarios.length}
              placeholder="Select Scenario"
            />
          </Grid>


          <Grid item xs={12} md={4}>
            <EnumSelect
              label="Customer Type"
              options={customerTypeOptions}
              value={formData.simulation?.customer_type}
              onChange={(value) => set("simulation.customer_type", value)}
              placeholder="Customer Type"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <EnumSelect
              label="Channel"
              loading={!channelOptions.length}
              options={channelOptions}
              value={formData.simulation?.channel}
              onChange={(value) => set("simulation.channel", value)}
              placeholder="Channel"
            />
          </Grid>
        </Grid>
             <Grid container spacing={2.5} my={2}>
          {[
            ["Taxes", "simulation.taxes"],
            ["Doorstep Fee", "simulation.doorstep_fee"],
            ["Existing Discounts", "simulation.existing_discounts"],
            ["Loyalty Points Applied", "simulation.loyalty_points_applied"],
          ].map(([label, path]) => (
            <Grid item xs={12} md={3} key={path}>
              <TextInput
                label={label}
                value={getByPath(formData, path) ?? ""}
                onChange={(value) => set(path, value)}
              />
            </Grid>
          ))}
        </Grid>
             <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            my: 2,
          }}
        >
          <Typography>Cart Items</Typography>
          <Button
            size="small"
            onClick={addCartItem}
            sx={{ textTransform: "none", color: C.teal }}
          >
            + Add Item
          </Button>
        </Box>

        <Grid container spacing={2}>
          {(formData.simulation?.cart_items ?? []).map((item, index) => (
            <Grid item xs={12} key={`${item.product_id || "new"}-${index}`}>
              <Box sx={{ border: `1px solid ${C.border}`, borderRadius: 2, p: 2 }}>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={12} sm={2}>
                    <TextInput
                      label="Product ID"
                      value={item.product_id}
                      onChange={(value) => updateCartItem(index, "product_id", value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextInput
                      label="Name"
                      value={item.name}
                      onChange={(value) => updateCartItem(index, "name", value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextInput
                      label="Price"
                      value={item.price}
                      onChange={(value) => updateCartItem(index, "price", value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextInput
                      label="Quantity"
                      value={item.quantity}
                      onChange={(value) => updateCartItem(index, "quantity", value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      size="small"
                      onClick={() => removeCartItem(index)}
                      sx={{ color: C.red, textTransform: "none", mt: 2 }}
                    >
                      Remove
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          ))}
        </Grid>
       
      </Card>

      <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
        <Button
          variant="contained"
          startIcon={
            running ? (
              <CircularProgress size={14} sx={{ color: C.bg }} />
            ) : (
              <PlayArrowIcon />
            )
          }
          onClick={onRunSimulation}
          disabled={
            running ||
            !formData.simulation?.scenario_template ||
            !formData.simulation?.customer_type ||
            !formData.simulation?.channel
          }
          sx={{
            background: C.text,
            color: C.bg,
            fontWeight: 700,
            fontSize: 12,
            textTransform: "none",
            "&.Mui-disabled": { background: C.text, color: C.white },
          }}
        >
          {running ? "Running..." : "Run Simulation"}
        </Button>
      </Box>

      {simResult === null && !running && (
        <Card>
          <SectionLabel n={2} label="Scenario Result" />
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                border: `2px dashed ${C.border}`,
                margin: "0 auto 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <InfoOutlinedIcon sx={{ color: C.muted, fontSize: 28 }} />
            </Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.text, mb: 1 }}>
              No simulation result yet
            </Typography>
            <Typography sx={{ fontSize: 12, color: C.muted, mb: 2, maxWidth: 620, mx: "auto" }}>
              Review the scenario above, then click Run Simulation to preview what applies, discount value, funding split, stacking logic, and profitability impact.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
              <Button
                startIcon={<ArrowBackIcon />}
                size="small"
                onClick={() => applyScenario(null)}
                sx={{
                  color: C.muted,
                  border: `1px solid ${C.border}`,
                  textTransform: "none",
                  fontSize: 12,
                }}
              >
                Reset Scenario
              </Button>
              <Button
                startIcon={<PlayArrowIcon />}
                variant="contained"
                size="small"
                onClick={onRunSimulation}
                disabled={
                  running ||
                  !formData.simulation?.scenario_template ||
                  !formData.simulation?.customer_type ||
                  !formData.simulation?.channel
                }
                sx={{
                  background: C.text,
                  color: C.bg,
                  fontWeight: 700,
                  fontSize: 12,
                  textTransform: "none",
                }}
              >
                Run Simulation
              </Button>
            </Box>
          </Box>
        </Card>
      )}

      {running && (
        <Card>
          <SectionLabel n={2} label="Scenario Result" />
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress sx={{ color: C.teal, mb: 2 }} />
            <Typography sx={{ fontSize: 13, color: C.muted }}>
              Running simulation...
            </Typography>
          </Box>
        </Card>
      )}

      {isFailed && !running && (
        <Card>
          <SectionLabel n={2} label="Scenario Result" />

          <Alert
            severity="error"
            icon={<Cancel />}
            sx={{
              mb: 2.5,
              background: "#FFF4F0",
              color: C.red,
              "& .MuiAlert-icon": { color: C.red },
              border: "1px solid #FC6C4F",
              borderRadius: 1.5,
            }}
          >
            <AlertTitle sx={{ color: C.text, fontSize: 13, fontWeight: 700 }}>
              Simulation Failed
            </AlertTitle>
            <Typography sx={{ fontSize: 12, color: C.muted }}>
              This incentive violates business rules and cannot be published yet.
            </Typography>
          </Alert>

          <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.text, mb: 1.5 }}>
            Failure Analysis
          </Typography>

          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Failure Reason", "Blocked Rule", "Impact", "Suggested Fix"].map(
                    (header) => (
                      <TableCell
                        key={header}
                        sx={{ color: C.muted, fontSize: 11, borderColor: C.border }}
                      >
                        {header}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {failureAnalysis.length > 0 ? (
                  failureAnalysis.map((item, index) => (
                    <TableRow key={`${item.reason || "failure"}-${index}`}>
                      <TableCell sx={{ color: C.text, fontSize: 12, borderColor: C.border, py: 2 }}>
                        <WarningAmberIcon sx={{ color: C.red, fontSize: 17, verticalAlign: "middle", mr: 0.5 }} />
                        {item.reason || "—"}
                      </TableCell>
                      <TableCell sx={{ color: C.text, fontSize: 12, borderColor: C.border, py: 2 }}>
                        {item.blocked_rule || "—"}
                      </TableCell>
                      <TableCell sx={{ color: C.text, fontSize: 12, borderColor: C.border, fontWeight: 600, py: 2 }}>
                        {item.impact || "—"}
                      </TableCell>
                      <TableCell sx={{ color: C.text, fontSize: 12, borderColor: C.border, py: 2 }}>
                        {item.suggested_fix || "—"}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ color: C.muted, fontSize: 12, borderColor: C.border }}>
                      No detailed failure analysis was returned.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>

          {optimizationHints.length > 0 && (
            <Grid container my={5} sx={{ borderBottom: `1px solid ${C.border}`, pb: 2.5 }}>
              <Grid item xs={12} md={6} sx={{ pr: { md: 2 } }}>
                <Box display="flex" gap={2} alignItems="center">
                  <LightbulbOutlined sx={{ color: "#FEB91E", fontSize: 20 }} />
                  <Typography fontSize={14}>Suggested Optimization Hints</Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                  {optimizationHints.map((hint, index) => (
                    <Box
                      key={`${hint}-${index}`}
                      sx={{
                        width: "100%",
                        px: 2,
                        py: 1,
                        border: "1px solid #FEB91E4D",
                        bgcolor: "#FFFAEA",
                        borderRadius: 1,
                        my: 1,
                      }}
                    >
                      <Typography fontSize={13}>{hint}</Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>

              {abusePreview && (
                <Grid item xs={12} md={6} sx={{ pl: { md: 2 } }}>
                  <Box display="flex" gap={2} alignItems="center">
                    <WarningAmberIcon sx={{ color: C.red, fontSize: 20 }} />
                    <Typography fontSize={14}>Abuse Probability Preview</Typography>
                  </Box>

                  <Grid container my={2} spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
              <AbuseProbabilityPreview
  data={simResult?.abuse_probability_preview}
/>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <Typography fontSize={13} fontWeight={600} sx={{ color: C.muted }}>
                        {abusePreview.message || "—"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
          )}

          {rerunOptions.length > 0 && (
            <Box my={2}>
              <Box display="flex" gap={1} alignItems="center">
                <PlayArrowIcon sx={{ color: C.text, fontSize: 20 }} />
                <Typography fontSize={14}>Re-run Simulation Options</Typography>
              </Box>

              <Box my={2} display="flex" alignItems="center" gap={2} flexWrap="wrap">
                {rerunOptions.includes("run_on_saved_scenario") && (
                  <Button
                    variant="outlined"
                    onClick={onRunSimulation}
                    disabled={running}
                    sx={{
                      border: `2px solid ${C.border}`,
                      px: 3,
                      py: 1,
                      textTransform: "none",
                    }}
                  >
                    <Box>
                      <Typography fontSize={13} sx={{ color: C.text }}>
                        Run on Saved Scenario
                      </Typography>
                      <Typography fontSize={11} sx={{ color: C.muted }}>
                        Re-run using the last saved scenario
                      </Typography>
                    </Box>
                  </Button>
                )}

                {rerunOptions.includes("use_previous_real_order") && (
                  <Button
                    variant="outlined"
                    disabled
                    sx={{
                      border: `2px solid ${C.border}`,
                      px: 3,
                      py: 1,
                      textTransform: "none",
                    }}
                  >
                    <Box>
                      <Typography fontSize={13} sx={{ color: C.text }}>
                        Use Previous Real Order
                      </Typography>
                      <Typography fontSize={11} sx={{ color: C.muted }}>
                        Available when the previous-order simulation endpoint is connected
                      </Typography>
                    </Box>
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Card>
      )}

      {isSuccessful && !running && (
        <Card>
          <SectionLabel n={2} label="Scenario Result" />

          <Alert
            severity="success"
            icon={<CheckCircleIcon />}
            sx={{
              mb: 2.5,
              background: "#ECF9F7",
              color: C.teal,
              "& .MuiAlert-icon": { color: C.teal },
              borderRadius: 1.5,
            }}
          >
            <AlertTitle sx={{ color: C.text, fontSize: 13, fontWeight: 700 }}>
              PASS — Incentive applied successfully
            </AlertTitle>
            <Typography sx={{ fontSize: 12, color: C.muted }}>
              All checks passed. This incentive can be safely published.
            </Typography>
          </Alert>

          {(() => {
            const summary = result?.summary ?? {};
            const funding = result?.funding_responsibility ?? {};

            const fundingValue = [
              funding?.platform?.label ||
                (funding?.platform?.amount !== undefined
                  ? `Platform ${funding.platform.amount} EGP (${funding.platform.percent ?? 0}%)`
                  : null),
              funding?.provider?.label ||
                (funding?.provider?.amount !== undefined
                  ? `Provider ${funding.provider.amount} EGP (${funding.provider.percent ?? 0}%)`
                  : null),
            ]
              .filter(Boolean)
              .join(" • ");

            const cards = [
              {
                label: "Applied Incentive",
                value: summary?.applied_incentive ?? "—",
              },
              {
                label: "Discount Applied",
                value:
                  summary?.discount_label ??
                  result?.discount_applied_label ??
                  (result?.discount_amount !== undefined
                    ? `${result.discount_amount} EGP`
                    : "—"),
                color: C.teal,
              },
              {
                label: "Final Total After Discount",
                value:
                  summary?.final_total !== undefined
                    ? `${summary.final_total} EGP`
                    : result?.final_total !== undefined
                      ? `${result.final_total} EGP`
                      : "—",
              },
              {
                label: "ROI Projection",
                value: result?.roi_projection ?? "—",
              },
              {
                label: "Margin Status",
                value: result?.margin_status ?? "—",
                color:
                  String(result?.margin_status ?? "").toLowerCase() === "healthy"
                    ? C.teal
                    : String(result?.margin_status ?? "").toLowerCase() === "at_risk"
                      ? C.amber
                      : C.text,
              },
              {
                label: "Loss Risk",
                value: result?.loss_risk ?? "—",
                color:
                  String(result?.loss_risk ?? "").toLowerCase() === "low"
                    ? C.teal
                    : C.text,
              },
              {
                label: "Stacking Conflict",
                value: result?.stacking_conflict ?? "—",
              },
              {
                label: "Taxes Excluded",
                value:
                  result?.taxes_excluded === true
                    ? "Yes"
                    : result?.taxes_excluded === false
                      ? "No"
                      : "—",
                color: result?.taxes_excluded === true ? C.teal : C.text,
              },
              {
                label: "Maximum Cap Check",
                value: result?.maximum_cap_check ?? "—",
                color:
                  String(result?.maximum_cap_check ?? "").toLowerCase() === "passed"
                    ? C.teal
                    : C.text,
              },
              {
                label: "Funding Responsibility",
                value: fundingValue || "—",
              },
            ];

            return (
              <Grid container spacing={2}>
                {cards.map((item) => (
                  <Grid item xs={12} sm={6} md={3} key={item.label}>
                    <Box
                      sx={{
                        borderRadius: 1.5,
                        p: 1.5,
                        display: "flex",
                        gap: 1,
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontSize: 11, color: C.muted }}>
                          {item.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: item.color || C.text,
                          }}
                        >
                          {item.value}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            );
          })()}
        </Card>
      )}

      {result && validationChecklist.length > 0 && !running && (
        <Card>
          <SectionLabel n={3} label="Simulation Validation" />
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {validationChecklist.map((item) => {
              const passed = item.status === "passed";
              return (
                <Box
                  key={item.key}
                  sx={{ flex: "1 1 140px", display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  {passed ? (
                    <CheckCircleIcon sx={{ color: C.teal, fontSize: 16, mt: 0.2 }} />
                  ) : (
                    <Cancel sx={{ color: C.red, fontSize: 16, mt: 0.2 }} />
                  )}
                  <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: C.text }}>
                      {item.title || "—"}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: C.muted }}>
                      {item.description || "—"}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Card>
      )}

      {result?.exception_scenario_check && !running && (
        <Card>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <SectionLabel n={4} label="Exception Scenario Check" />
            <Chip
              label={result.exception_scenario_check.expected || "Expected Block"}
              size="small"
              sx={{ background: "#FFF7D9", color: C.text, fontSize: 11 }}
            />
          </Box>

          <Typography sx={{ fontSize: 12, color: C.muted, mb: 2 }}>
            Edge cases are tested to ensure the incentive does not apply incorrectly.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <Box sx={{ borderRadius: 1.5, p: 2, background: "#FAFAFA" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.text, mb: 1 }}>
                  Test Scenario: {result.exception_scenario_check.test_scenario || "—"}
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                  <Box>
                    <Typography sx={{ fontSize: 11, color: C.muted }}>Subtotal</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.text }}>
                      {result.exception_scenario_check.test_amount ?? "—"} EGP
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 11, color: C.muted }}>Minimum Required</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.text }}>
                      {result.exception_scenario_check.minimum_required ?? "—"} EGP
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={7}>
              <Box sx={{ marginInline: 2 }}>
                <Typography sx={{ fontSize: 12, color: C.text, mb: 0.5 }}>
                  <span className="fw-bold">Result:</span>{" "}
                  {result.exception_scenario_check.result || "—"}
                </Typography>
                <Typography sx={{ fontSize: 11, color: C.muted }}>
                  {result.exception_scenario_check.notice || "—"}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Card>
      )}
    </Box>
  );
}

// ============================================================================
// 10) Step 5 - Review & Publish (MOCK UI + REAL publish API)
// ============================================================================

function ReviewPublishStep({
  formData,
  set,
  enums,
  step5Options,
  step5Loading,
  step5Error,
  step5Result,
  submitError,
}) {
  const publishOptions = toOptions(step5Options?.publish_options);
  const rollbackStrategies = toOptions(step5Options?.rollback_strategies);
  const approvalWorkflows = toOptions(step5Options?.approval_workflows);
  const fallbackIncentives = (step5Options?.fallback_incentives ?? []).map(
    (item) => ({
      id: item?.id,
      label: item?.label || item?.internal_code || String(item?.id ?? ""),
    })
  );
  const approvers = (step5Options?.approvers ?? []).map((item) => ({
    id: item?.id,
    label: item?.label || item?.name || String(item?.id ?? ""),
  }));
  const recipientOptions = (step5Options?.notification_recipient_suggestions ?? []).map(
    (item) => ({
      id: item,
      label: item,
    })
  );

  const customerSegments = getSelectedLabels(
    formData.eligibility?.customer_segment ?? [],
    enums?.customer_segments ?? []
  );
  const regions = getSelectedLabels(
    formData.eligibility?.region ?? [],
    enums?.regions?.length ? enums.regions : REGION_FALLBACK_OPTIONS
  );
  const channels = getSelectedLabels(
    formData.eligibility?.order_channel ?? [],
    enums?.channels ?? []
  );

  const typeLabel =
    enums?.types?.find((item) => item.id === formData.type)?.label ||
    formData.type ||
    "—";

  const statusLabel =
    enums?.statuses?.find((item) => item.id === formData.status)?.label ||
    formData.status ||
    "—";

  const fundingSource = mapFundingSourceToStep5(formData.funding?.source);
  const fundingLabel =
    fundingSource === "platform"
      ? "Platform"
      : fundingSource === "provider"
        ? "Provider"
        : fundingSource === "shared"
          ? "Shared"
          : formData.funding?.source || "—";

  const stackBehaviorLabel =
    STACK_BEHAVIOR_OPTIONS.find((item) => item.id === formData.stackBehavior)?.label ||
    formData.stackBehavior ||
    "—";

  const excluded = formData.conflictRule?.excluded_items ?? [];
  const exclusions = [
    excluded.includes("taxes") ? "Taxes Excluded" : null,
    excluded.includes("doorstep_fee") ? "Doorstep Fee Excluded" : null,
  ]
    .filter(Boolean)
    .join(" · ") || "None";

  const formatNumber = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    const number = Number(value);
    return Number.isFinite(number) ? number.toLocaleString() : String(value);
  };

  const formatMoney = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    const number = Number(value);
    return Number.isFinite(number) ? `${number.toLocaleString()} EGP` : String(value);
  };

  const startDate = formData.starts_at
    ? dayjs(formData.starts_at).format("MMM D")
    : "—";
  const endDate = formData.ends_at
    ? dayjs(formData.ends_at).format("MMM D")
    : "—";

  const finalImpact = step5Result?.final_impact_snapshot ?? null;
  const finalChecks = step5Result?.final_checks ?? null;
  const activityLog = Array.isArray(step5Result?.activity_log_preview)
    ? step5Result.activity_log_preview
    : [];
  const rollbackPreparation = step5Result?.rollback_preparation ?? null;
  const publishSummary = step5Result?.publish_summary ?? null;

  return (
    <Box>
      <Alert
        severity="success"
        icon={<CheckCircleIcon />}
        sx={{
          mb: 2.5,
          background: "#ECF9F7",
          color: C.text,
          "& .MuiAlert-icon": { color: C.teal },
          border: `1px solid ${C.teal}`,
          borderRadius: 1.5,
        }}
      >
        <AlertTitle sx={{ color: C.text, fontSize: 13, fontWeight: 700 }}>
          Ready for Review
        </AlertTitle>
        <Typography sx={{ fontSize: 12, color: C.muted }}>
          This incentive passed setup validation and simulation. Review the configuration before publishing.
        </Typography>
      </Alert>

      {step5Error && (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          {step5Error}
        </Alert>
      )}

      {submitError && (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          {submitError}
        </Alert>
      )}

      <Card>
        <SectionLabel n={1} label="Configuration Summary" />
        <Grid container spacing={2}>
          {[
            {
              title: "Basic Info",
              lines: [formData.title || "—", typeLabel, statusLabel],
            },
            {
              title: "Reward Rules",
              lines: [
                benefitTypeLabel(formData.benefit?.benefit_type, formData.benefit),
                `${formatMoney(formData.benefit?.max_cap)} cap`,
                `Min order ${formatMoney(formData.eligibility?.min_order_value)}`,
              ],
            },
            {
              title: "Eligibility",
              lines: [
                customerSegments.join(" + ") || "—",
                regions.join(" + ") || "—",
                channels.join(" + ") || "—",
              ],
            },
            {
              title: "Validity",
              lines: [
                `${startDate} – ${endDate}`,
                formData.recurrence || "—",
                formatTimeWindow(
                  formData.time_window_start,
                  formData.time_window_end
                ) || "All Day",
              ],
            },
            {
              title: "Budget & Funding",
              lines: [
                formatMoney(formData.budget?.total_budget),
                fundingLabel,
                `Platform ${formatNumber(formData.funding?.platform_share_percentage)}% / Provider ${formatNumber(formData.funding?.provider_share_percentage)}%`,
              ],
            },
            {
              title: "Safeguards",
              lines: [
                stackBehaviorLabel,
                exclusions,
                `Margin Threshold ${formatNumber(formData.conflictRule?.protected_margin_threshold)}%`,
              ],
            },
          ].map((section) => (
            <Grid item xs={12} md={4} key={section.title}>
              <Box sx={{ borderRadius: 1.5, p: 2, display: "flex", gap: 1 }}>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    minWidth: 24,
                    borderRadius: "50%",
                    background: "#F8F8F8",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.text, mb: 1 }}>
                    {section.title}
                  </Typography>
                  {section.lines.map((line, index) => (
                    <Typography key={`${section.title}-${index}`} sx={{ fontSize: 11, color: C.muted }}>
                      {line}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Card>

      <Card>
        <SectionLabel n={2} label="Final Impact Snapshot (Based on Simulation)" />
        {!finalImpact ? (
          <Typography sx={{ fontSize: 12, color: C.muted }}>
            Final impact snapshot will appear after the Step 5 publish request is saved.
          </Typography>
        ) : (
          <Grid container spacing={1.5}>
            {[
              { label: "Expected Reach", value: finalImpact.expected_reach },
              { label: "Revenue Impact", value: formatMoney(finalImpact.revenue_impact) },
              { label: "Discount Spend", value: formatMoney(finalImpact.discount_spend) },
              { label: "ESTBN Cost", value: finalImpact.estbn_cost_label || formatMoney(finalImpact.estbn_cost) },
              { label: "Provider Cost", value: finalImpact.provider_cost_label || formatMoney(finalImpact.provider_cost) },
              { label: "Abuse Risk", value: finalImpact.abuse_risk },
            ].map((item) => (
              <Grid item xs={6} md={2} key={item.label}>
                <Box
                  sx={{
                    border: "1px solid #D8D8D8",
                    borderRadius: 1.5,
                    p: 1.5,
                    minHeight: 80,
                    width: "100%",
                    display: "flex",
                    gap: 1,
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: 10, color: C.muted, mb: 0.5 }}>
                      {item.label}
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                      {item.value ?? "—"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Card>

      <Card>
        <SectionLabel n={3} label="Approval & Publish Settings" />

        {step5Loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={16} sx={{ color: C.teal }} />
            <Typography sx={{ fontSize: 12, color: C.muted }}>
              Loading Step 5 options...
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 2.5 }}>
              <FieldLabel required>Publish Option</FieldLabel>
              <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                {publishOptions.map((option) => {
                  const selected = formData.publishOption === option.id;
                  return (
                    <Grid item xs={12} md={4} key={option.id}>
                      <Box
                        onClick={() => set("publishOption", option.id)}
                        sx={{
                          p: 2,
                          borderRadius: 1.5,
                          border: `1.5px solid ${selected ? C.teal : C.border}`,
                          background: selected ? "#ECF9F7" : "transparent",
                          cursor: "pointer",
                          transition: "all .2s",
                          "&:hover": { borderColor: C.teal },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Radio
                            checked={selected}
                            size="small"
                            sx={{ p: 0, color: C.border, "&.Mui-checked": { color: C.teal } }}
                          />
                          <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.text }}>
                              {option.title || option.label}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: C.muted }}>
                              {option.label}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}

                {formData.publishOption === "schedule" && (
                  <Grid item xs={12} md={4}>
                    <FieldLabel required>Scheduled Publish Date</FieldLabel>
                    <DateTimePicker
                      format="DD,MM,YYYY HH:mm"
                      value={
                        formData.scheduledPublishDate
                          ? dayjs(formData.scheduledPublishDate)
                          : null
                      }
                      onChange={(date) =>
                        set(
                          "scheduledPublishDate",
                          date && date.isValid()
                            ? date.format("YYYY-MM-DD HH:mm:ss")
                            : null
                        )
                      }
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                        },
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>

            <Box sx={{ mt: 2.5 }}>
              <FieldLabel required>Approval Workflow</FieldLabel>
              <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                {approvalWorkflows.map((option) => {
                  const selected = formData.approvalWorkflow === option.id;
                  return (
                    <Grid item xs={12} md={4} key={option.id}>
                      <Box
                        onClick={() => set("approvalWorkflow", option.id)}
                        sx={{
                          p: 2,
                          borderRadius: 1.5,
                          border: `1.5px solid ${selected ? C.teal : C.border}`,
                          background: selected ? "#ECF9F7" : "transparent",
                          cursor: "pointer",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Radio
                            checked={selected}
                            size="small"
                            sx={{ p: 0, color: C.border, "&.Mui-checked": { color: C.teal } }}
                          />
                          <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.text }}>
                              {option.title || option.label}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: C.muted }}>
                              {option.label}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>

              {formData.approvalWorkflow !== "auto_approve" && (
                <Box sx={{ mt: 2 }}>
                  <EnumSelect
                    label="Approver"
                    options={approvers}
                    value={formData.approver}
                    onChange={(value) => set("approver", value)}
                    placeholder="Select approver"
                  />
                </Box>
              )}
            </Box>

            <Box sx={{ mt: 2.5 }}>
              <MultiEnumSelect
                label="Notification Recipients"
                options={recipientOptions}
                value={formData.notificationRecipients}
                onChange={(value) => set("notificationRecipients", value)}
                placeholder="Select recipients"
              />
            </Box>
          </>
        )}
      </Card>

      <Card sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ShieldCheck sx={{ color: C.text, fontSize: 18 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.text }}>
              Final Checks
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 12, color: C.teal, fontWeight: 600 }}>
            {finalChecks?.completed || "Pending"}
          </Typography>
        </Box>

        {!finalChecks ? (
          <Typography sx={{ fontSize: 12, color: C.muted }}>
            Final checks will appear after the Step 5 publish request is saved.
          </Typography>
        ) : (
          (finalChecks.items ?? []).map((item, index, items) => (
            <Box
              key={item.label || index}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: index < items.length - 1 ? 1.2 : 0,
              }}
            >
              <Typography sx={{ fontSize: 12, color: C.muted }}>
                {item.label || "—"}
              </Typography>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  background: item.passed ? C.teal : C.red,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {item.passed ? (
                  <CheckCircleIcon sx={{ fontSize: 13, color: C.bg }} />
                ) : (
                  <Cancel sx={{ fontSize: 13, color: C.bg }} />
                )}
              </Box>
            </Box>
          ))
        )}
      </Card>

      <Card>
        <SectionLabel n={6} label="Activity Log Preview" />
        {activityLog.length === 0 ? (
          <Typography sx={{ fontSize: 12, color: C.muted }}>
            Activity log preview will appear after the Step 5 publish request is saved.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", overflowX: "auto" }}>
            {activityLog.map((item, index) => (
              <Box key={`${item.action}-${index}`} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CheckCircleIcon
                    sx={{ color: item.status === "completed" || item.status === "active" ? C.teal : C.border, fontSize: 22 }}
                  />
                  <Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.text }}>
                      {item.action || "—"}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: C.muted }}>
                      {item.description || "—"}
                    </Typography>
                  </Box>
                </Box>
                {index < activityLog.length - 1 && <ArrowRightAltIcon />}
              </Box>
            ))}
          </Box>
        )}
      </Card>

      <Card>
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={6}>
            <SectionLabel n={7} label="Rollback Preparation" />
            {!rollbackPreparation ? (
              <Typography sx={{ fontSize: 12, color: C.muted }}>
                Rollback information will appear after the Step 5 publish request is saved.
              </Typography>
            ) : (
              <Box sx={{ display: "grid", gap: 1.2 }}>
                <Box>
                  <Typography sx={{ fontSize: 10, color: C.muted }}>Rollback Strategy</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{rollbackPreparation.rollback_strategy_label || rollbackPreparation.rollback_strategy || "—"}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 10, color: C.muted }}>Fallback Incentive ID</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{rollbackPreparation.fallback_incentive_id ?? "—"}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 10, color: C.muted }}>Rollback Notes</Typography>
                  <Typography sx={{ fontSize: 12, color: C.muted }}>{rollbackPreparation.rollback_notes || "—"}</Typography>
                </Box>
              </Box>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionLabel n={8} label="Emergency Publish Notes" />
            <Typography sx={{ fontSize: 12, color: C.muted }}>
              {step5Result?.emergency_notes || "No emergency notes provided."}
            </Typography>
          </Grid>
        </Grid>
      </Card>

      {/* Applied Rules & Policies will be added when the backend includes it in Step 5 response. */}
    </Box>
  );
}

function benefitTypeLabel(benefitType, benefit = {}) {
  if (benefitType === "percentage_discount") {
    return `${benefit.percentage ?? "—"}% discount`;
  }
  if (benefitType === "fixed_discount") {
    return `${formatMoneyValue(benefit.amount)} discount`;
  }
  return benefitType || "—";
}

function formatMoneyValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  const number = Number(value);
  return Number.isFinite(number) ? `${number.toLocaleString()} EGP` : String(value);
}

// ============================================================================
// 11) Main component
// ============================================================================

export default function CreateIncentive() {
  const { user } = useSelector((state) => state.auth);
  const token = user?.token ? `Bearer ${user.token}` : "";

  const { enums, dropdowns, rawEnums, loading: enumsLoading, error: enumsError } =
    useIncentiveEnums(token);
  const { vehicleTypes, loading: vehicleTypesLoading } = useVehicleTypes(token);
  const { categories, loading: categoriesLoading } = useCategories(token);

  const [step, setStep] = useState(STEP.BASIC_INFO);
  // Step 1 returns the incentiveId. Steps 2, 3 and 4 use the same ID.
  const [incentiveId, setIncentiveId] = useState(null);
  const [formData, setFormData] = useState(cloneInitialFormData());
  const [stepResponses, setStepResponses] = useState({});

  const [simResult, setSimResult] = useState(null);
  const [simulationCompleted, setSimulationCompleted] = useState(false);
  const [runningSimulation, setRunningSimulation] = useState(false);

  const [step5Options, setStep5Options] = useState(null);
  const [step5Loading, setStep5Loading] = useState(false);
  const [step5Error, setStep5Error] = useState(null);
  const [step5Result, setStep5Result] = useState(null);

  const [savingStep, setSavingStep] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // One update function for ALL fields.
  const set = (path, value) => {
    setFormData((previous) => setPath(previous, path, value));
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // ------------------------------------------------------------
  // Step 5 options: REAL GET API, loaded when entering Review & Publish.
  // ------------------------------------------------------------
  useEffect(() => {
    if (step !== STEP.REVIEW || !token) return;

    let mounted = true;
    setStep5Loading(true);
    setStep5Error(null);

    axios
      .get(STEP5_OPTIONS_URL, {
        headers: { Authorization: token },
      })
      .then((response) => {
        if (!mounted) return;

        const data = response?.data?.data ?? response?.data ?? {};
        setStep5Options(data);

        const defaults = data?.defaults ?? {};
        if (!formData.publishOption && defaults.publish_option) {
          set("publishOption", defaults.publish_option);
        }
        if (!formData.approvalWorkflow && defaults.approval_workflow) {
          set("approvalWorkflow", defaults.approval_workflow);
        }
        if (!formData.rollback?.strategy && defaults.rollback_strategy) {
          set("rollback.strategy", defaults.rollback_strategy);
        }
      })
      .catch((error) => {
        if (!mounted) return;
        const message = getErrorMessage(error);
        setStep5Options(null);
        setStep5Error(message);
      })
      .finally(() => {
        if (!mounted) return;
        setStep5Loading(false);
      });

    return () => {
      mounted = false;
    };
  }, [step, token]);

  // ------------------------------------------------------------
  // Step 1 save: REAL API
  // ------------------------------------------------------------
  const saveBasicInfo = async () => {
    const validationErrors = validateBasicInfo(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      showSnackbar("Please complete the required fields.", "error");
      return false;
    }

    setSavingStep(true);
    setSubmitError(null);

    try {
      const payload = buildStep1Payload(formData);

      const response = await axios.post(STEP1_CREATE_URL, payload, {
        headers: { Authorization: token },
      });

      const savedId = getSavedId(response);
      if (savedId === null || savedId === undefined) {
        showSnackbar("Basic Info was saved, but incentive ID was not returned.", "error");
        return false;
      }

      setIncentiveId(savedId);
      setStepResponses((previous) => ({ ...previous, [STEP.BASIC_INFO]: getApiStepData(response) }));
      setStep(STEP.RULES);
      setErrors({});
      showSnackbar("Basic Info saved successfully.");
      return true;
    } catch (error) {
      const message = getErrorMessage(error);
      setSubmitError(message);
      showSnackbar(message, "error");
      return false;
    } finally {
      setSavingStep(false);
    }
  };

  // ------------------------------------------------------------
  // Step 2 save: REAL API
  // ------------------------------------------------------------
  const saveRulesEligibility = async () => {
    if (incentiveId === null || incentiveId === undefined) {
      showSnackbar("Incentive ID is missing. Please save Basic Info first.", "error");
      return false;
    }

    setSavingStep(true);
    setSubmitError(null);

    try {
      const vehicleTypeOptions = vehicleTypes;
      const apiRegionOptions = getEnumOptions(enums, ["regions"]);
      const regionOptions = apiRegionOptions.length
        ? apiRegionOptions
        : getDropdownOptions(dropdowns, ["regions"]).length
          ? getDropdownOptions(dropdowns, ["regions"])
          : REGION_FALLBACK_OPTIONS;
      const serviceCategoryOptions = getDropdownOptions(dropdowns, ["categories"]);

      const payload = buildStep2Payload(formData, {
        vehicleTypes: vehicleTypeOptions,
        serviceCategories: serviceCategoryOptions,
        regions: regionOptions,
        incentiveId,
      });

      const response = await axios.post(STEP2_RULES_URL, payload, {
        headers: { Authorization: token },
      });
      setStepResponses((previous) => ({ ...previous, [STEP.RULES]: getApiStepData(response) }));
      setStep(STEP.BUDGET);
      showSnackbar("Rules & Eligibility saved successfully.");
      return true;
    } catch (error) {
      const message = getErrorMessage(error);
      setSubmitError(message);
      showSnackbar(message, "error");
      return false;
    } finally {
      setSavingStep(false);
    }
  };

  // ------------------------------------------------------------
  // Step 3 validation
  // ------------------------------------------------------------
  const validateBudgetFunding = (data) => {
    const required = [
      [data.budget?.total_budget, "Total Budget"],
      [data.budget?.daily_budget_cap, "Daily Budget Cap"],
      [data.budget?.monthly_budget_cap, "Monthly Budget Cap"],
      [data.budget?.redemption_cost_model, "Redemption Cost Model"],
      [data.budget?.estimated_cost_per_redemption, "Estimated Cost per Redemption"],
      [data.funding?.source, "Funding Source"],
      [data.funding?.wallet, "Funding Wallet ID"],
      [data.funding?.cost_center, "Cost Center"],
      // The supplied Step 3 UI marks only the core budget/funding inputs as required.
    ];

    const missing = required
      .filter(([value]) => value === "" || value === null || value === undefined)
      .map(([, label]) => label);

    if (missing.length > 0) {
      showSnackbar(`Please complete: ${missing.slice(0, 4).join(", ")}.`, "error");
      return false;
    }

    return true;
  };

  // ------------------------------------------------------------
  // Step 3 save: REAL API
  // Step 3 is also where the incentiveId is returned for Simulation.
  // ------------------------------------------------------------
  const saveBudgetFunding = async () => {
    if (incentiveId === null || incentiveId === undefined) {
      showSnackbar("Incentive ID is missing. Please save Basic Info first.", "error");
      return false;
    }

    if (!validateBudgetFunding(formData)) {
      return false;
    }

    setSavingStep(true);
    setSubmitError(null);

    try {
      const payload = buildStep3Payload(formData, incentiveId);
      const response = await axios.post(STEP3_BUDGET_FUNDING_URL, payload, {
        headers: { Authorization: token },
      });
      setStepResponses((previous) => ({ ...previous, [STEP.BUDGET]: getApiStepData(response) }));
      setStep(STEP.SIMULATION);
      setErrors({});
      showSnackbar("Budget & Funding saved successfully.");
      return true;
    } catch (error) {
      const message = getErrorMessage(error);
      setSubmitError(message);
      showSnackbar(message, "error");
      return false;
    } finally {
      setSavingStep(false);
    }
  };

  // ------------------------------------------------------------
  // Mock save for the remaining steps until their APIs are provided.
  // ------------------------------------------------------------
  const saveMockStep = async () => {
    await Promise.resolve();
    setStep((previousStep) => Math.min(STEP.REVIEW, previousStep + 1));
    showSnackbar("Step saved locally (mock API for now).");
    return true;
  };

  // ------------------------------------------------------------
  // Next button: one place controls ALL step transitions.
  // ------------------------------------------------------------
  const handleNext = async () => {
    if (step === STEP.BASIC_INFO) {
      await saveBasicInfo();
      return;
    }

    if (step === STEP.RULES) {
      await saveRulesEligibility();
      return;
    }

    if (step === STEP.BUDGET) {
      await saveBudgetFunding();
      return;
    }

    if (step === STEP.SIMULATION) {
      if (!simulationCompleted) {
        showSnackbar("Run the simulation successfully before continuing.", "error");
        return;
      }

      await saveMockStep();
    }
  };

  // ------------------------------------------------------------
  // Back: ONLY changes the current step.
  // formData is untouched, so the previous values stay there.
  // ------------------------------------------------------------
  const handleBack = () => {
    setStep((previousStep) => Math.max(STEP.BASIC_INFO, previousStep - 1));
  };

  // ------------------------------------------------------------
  // Step 4 save/run: REAL API
  // ------------------------------------------------------------
  const runSimulation = async () => {
    if (runningSimulation) return;

    if (incentiveId === null || incentiveId === undefined) {
      showSnackbar("Incentive ID is missing. Please complete the previous steps first.", "error");
      return;
    }

    setRunningSimulation(true);
    setSimResult(null);
    setSimulationCompleted(false);
    setSubmitError(null);

    try {
      const payload = buildSimulationPayload(formData, incentiveId);
      const response = await axios.post(STEP4_SIMULATION_URL, payload, {
        headers: { Authorization: token },
      });

      const result = response?.data?.data?.data ?? response?.data?.data ?? response?.data ?? {};
      const passed =
        result?.status === "success" ||
        result?.status === "passed" ||
        result?.simulation_status === "passed" ||
        result?.simulation_passed === true ||
        result?.passed === true;

      setSimResult(result);
      setSimulationCompleted(Boolean(passed));
      showSnackbar(
        passed ? "Simulation completed successfully." : "Simulation failed. Review the failure analysis.",
        passed ? "success" : "error"
      );
    } catch (error) {
      const errorResult =
        error?.response?.data?.data?.data ??
        error?.response?.data?.data ??
        error?.response?.data ??
        null;

      if (errorResult?.status === "failed" || errorResult?.simulation_passed === false) {
        setSimResult(errorResult);
        setSimulationCompleted(false);
        setSubmitError(null);
        showSnackbar("Simulation failed. Review the failure analysis.", "error");
      } else {
        const message = getErrorMessage(error);
        setSubmitError(message);
        setSimulationCompleted(false);
        showSnackbar(message, "error");
      }
    } finally {
      setRunningSimulation(false);
    }
  };

  // ------------------------------------------------------------
  // Optional local draft button.
  // Step saving itself is handled by Next / the step API.
  // ------------------------------------------------------------
  const handleSaveDraft = () => {
    try {
      localStorage.setItem(
        DRAFT_STORAGE_KEY,
        JSON.stringify(formData)
      );
      showSnackbar("Draft saved locally.");
    } catch {
      showSnackbar("Could not save the local draft.", "error");
    }
  };

  // ------------------------------------------------------------
  // Step 5 save/publish: REAL POST API.
  // No navigation or other side effect is performed after success.
  // ------------------------------------------------------------
  const handlePublish = async () => {
    if (!formData.publishOption) {
      showSnackbar("Please select a publish option.", "error");
      return;
    }

    if (
      formData.publishOption === "schedule" &&
      !formData.scheduledPublishDate
    ) {
      showSnackbar("Please select scheduled publish date and time.", "error");
      return;
    }

    if (!formData.approvalWorkflow) {
      showSnackbar("Please select an approval workflow.", "error");
      return;
    }

    if (
      formData.approvalWorkflow !== "auto_approve" &&
      (formData.approver === "" || formData.approver === null || formData.approver === undefined)
    ) {
      showSnackbar("Please select an approver for the selected workflow.", "error");
      return;
    }

    if ((formData.notificationRecipients ?? []).length === 0) {
      showSnackbar("Please select at least one notification recipient.", "error");
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const payload = buildStep5Payload(formData);
      const response = await axios.post(STEP5_REVIEW_PUBLISH_URL, payload, {
        headers: { Authorization: token },
      });

      const result =
        response?.data?.data?.data ??
        response?.data?.data ??
        response?.data ??
        {};

      setStep5Result(result);
      setStepResponses((previous) => ({
        ...previous,
        [STEP.REVIEW]: result,
      }));
      setIncentiveId((previousId) => previousId ?? getSavedId(response));
      localStorage.removeItem(DRAFT_STORAGE_KEY);
      showSnackbar("Step 5 saved successfully.");
    } catch (error) {
      const message = getErrorMessage(error);
      setSubmitError(message);
      showSnackbar(message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="m-4 min-vh-100">
        <div className="w-100">
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: C.text }}>
            Create Incentive
          </Typography>

          {enumsError && (
            <Alert severity="warning" sx={{ my: 2 }}>
              Couldn&apos;t load enums from the server. Check the API response
              before using the enum-based fields.
            </Alert>
          )}

          <Box sx={{ mb: 4 }}>
            <Stepper
              activeStep={step}
              connector={<QontoConnector />}
              alternativeLabel
            >
              {STEPS.map((label, index) => (
                <Step key={label} completed={index < step}>
                  <StepLabel
                    slots={{ stepIcon: StepIcon }}
                    slotProps={{ stepIcon: { icon: index + 1 } }}
                    sx={{
                      "& .MuiStepLabel-label": {
                        color:
                          index === step
                            ? C.teal
                            : index < step
                              ? C.text
                              : C.muted,
                        fontSize: 12,
                        fontWeight: index === step ? 700 : 400,
                        mt: 0.5,
                      },
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={9}>
              {step === STEP.BASIC_INFO && (
                <BasicInfoStep
                  formData={formData}
                  set={set}
                  enums={enums}
                  categories={categories}
                  enumsLoading={enumsLoading}
                  categoriesLoading={categoriesLoading}
                  errors={errors}
                />
              )}

              {step === STEP.RULES && (
                <RulesEligibilityStep
                  formData={formData}
                  set={set}
                  enums={enums}
                  dropdowns={dropdowns}
                  vehicleTypes={vehicleTypes}
                  enumsLoading={enumsLoading}
                  vehicleTypesLoading={vehicleTypesLoading}
                  apiData={stepResponses?.[STEP.RULES]}
                />
              )}

              {step === STEP.BUDGET && (
                <BudgetFundingStep
                  formData={formData}
                  set={set}
                  enums={enums}
                  dropdowns={dropdowns}
                  enumsLoading={enumsLoading}
                  apiData={stepResponses?.[STEP.BUDGET]}
                />
              )}

              {step === STEP.SIMULATION && (
                <SimulationStep
                  formData={formData}
                  set={set}
                  enums={enums}
                  rawEnums={rawEnums}
                  running={runningSimulation}
                  simResult={simResult}
                  simulationCompleted={simulationCompleted}
                  onRunSimulation={runSimulation}
                />
              )}

              {step === STEP.REVIEW && (
                <ReviewPublishStep
                  formData={formData}
                  set={set}
                  enums={enums}
                  step5Options={step5Options}
                  step5Loading={step5Loading}
                  step5Error={step5Error}
                  step5Result={step5Result}
                  submitError={submitError}
                />
              )}
            </Grid>

            <Grid item xs={12} md={3}>
              <SetupSummary
                step={step}
                formData={formData}
                enums={enums}
                simResult={simResult}
                simulationCompleted={simulationCompleted}
                stepResponses={stepResponses}
              />
            </Grid>
          </Grid>

          <div className="fixed-footer d-flex justify-content-between align-items-center">
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              disabled={step === STEP.BASIC_INFO || savingStep || submitting}
              sx={{
                color: C.muted,
                border: `1px solid ${C.border}`,
                textTransform: "none",
                fontSize: 13,
                px: 2.5,
                "&:hover": {
                  borderColor: C.teal,
                  color: C.teal,
                },
                "&.Mui-disabled": {
                  color: C.border,
                },
              }}
            >
              Back
            </Button>

            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button
                startIcon={<SaveIcon />}
                onClick={handleSaveDraft}
                disabled={savingStep || submitting}
                sx={{
                  color: C.muted,
                  border: `1px solid ${C.border}`,
                  textTransform: "none",
                  fontSize: 13,
                  px: 2.5,
                  "&:hover": {
                    borderColor: C.teal,
                    color: C.teal,
                  },
                }}
              >
                Save Draft
              </Button>

              {step === STEP.REVIEW ? (
                <Button
                  variant="contained"
                  startIcon={
                    submitting ? (
                      <CircularProgress size={16} sx={{ color: C.bg }} />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  onClick={handlePublish}
                  disabled={submitting}
                  sx={{
                    background: C.text,
                    color: C.bg,
                    fontWeight: 700,
                    fontSize: 13,
                    textTransform: "none",
                    px: 3,
                    "&:hover": { background: "#474b4b" },
                  }}
                >
                  {submitting ? "Publishing..." : "Publish Now"}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={savingStep || (step === STEP.SIMULATION && !simulationCompleted)}
                  sx={{
                    background: C.text,
                    color: C.bg,
                    fontWeight: 700,
                    fontSize: 13,
                    textTransform: "none",
                    px: 3,
                    "&:hover": { background: "#474b4b" },
                    "&.Mui-disabled": {
                      background: "#9a9a9a",
                      color: C.white,
                    },
                  }}
                >
                  {savingStep ? "Saving..." : "Next Step →"}
                </Button>
              )}
            </Box>
          </div>
        </div>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() =>
            setSnackbar((previous) => ({ ...previous, open: false }))
          }
          message={snackbar.message}
        />
      </div>
    </LocalizationProvider>
  );
}
